import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import './fonts.css'; // Custom font styles
import { Button } from './components/ui/button';
import { Card } from './components/ui/card';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Slider } from './components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { toast, Toaster } from 'sonner';
import { Upload, Zap, Target, Calculator } from 'lucide-react';
import MoveNetBodyProcessor from './components/MoveNetBodyProcessor';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import * as tf from '@tensorflow/tfjs-core';
import '@tensorflow/tfjs-backend-webgl';
import '@tensorflow/tfjs-backend-cpu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // Form state
  const [treatments, setTreatments] = useState(10);
  const [dietCalories, setDietCalories] = useState([2500]);
  const [sportHours, setSportHours] = useState([0]);
  const [currentWeight, setCurrentWeight] = useState('');
  const [height, setHeight] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  
  // Camera state
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [captureMode, setCaptureMode] = useState('camera'); // 'camera' or 'upload' - Kamera als Standard
  
  // Real-time pose guidance state
  const [poseGuidance, setPoseGuidance] = useState({
    bodyDetected: false,
    centered: false,
    distanceOk: false,
    fullBodyVisible: false,
    message: 'Warten auf Körpererkennung...',
    instruction: 'Stellen Sie sich vor die Kamera',
    bodyCoverage: 0,
    centerOffset: 0
  });
  const [isAnalyzingPose, setIsAnalyzingPose] = useState(false);
  
  // Auto-capture state
  const [autoCapture, setAutoCapture] = useState(true); // Auto-Capture aktiviert
  const [captureCountdown, setCaptureCountdown] = useState(null); // 3, 2, 1, null
  const autoCaptureTimerRef = useRef(null);
  const hasAutoCapturedRef = useRef(false); // Verhindert mehrfaches Auslösen
  const isCountdownRunningRef = useRef(false); // Verhindert mehrfachen Countdown-Start
  
  // Result state
  const [transformationResult, setTransformationResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transformedImage, setTransformedImage] = useState(null);
  const [showResultsOnly, setShowResultsOnly] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // Body detection state
  const [bodyDetected, setBodyDetected] = useState(false);
  const [bodyWithinBoundaries, setBodyWithinBoundaries] = useState(false);
  const [bodyDetectionMessage, setBodyDetectionMessage] = useState('');
  const [detectingBody, setDetectingBody] = useState(false);
  
  // Tutorial and Progress state
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showTooltip, setShowTooltip] = useState(null);
  const [showPhotoGuide, setShowPhotoGuide] = useState(false);
  
  // Urgency countdown state
  const [countdownTime, setCountdownTime] = useState(15 * 60); // 15 minutes
  const [showUrgencyBanner, setShowUrgencyBanner] = useState(true);
  
  // Lead generation state
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    mobile: '',
    email: ''
  });
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  
  // Refs
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const captureCanvasRef = useRef(null);
  const bodyDetectionCanvasRef = useRef(null);
  const moveNetProcessor = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const videoIdRef = useRef(null); // Unique ID für jede Video-Session
  const chunkCounterRef = useRef(0); // Track chunk number

  // Initialize MoveNet processor
  useEffect(() => {
    moveNetProcessor.current = new MoveNetBodyProcessor();
    return () => {
      if (moveNetProcessor.current) {
        moveNetProcessor.current.dispose();
      }
      // Cleanup camera stream
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Pre-initialize TensorFlow.js backend AND MoveNet processor on app mount
  useEffect(() => {
    async function initializeTensorFlow() {
      try {
        console.log('🔄 Pre-initializing TensorFlow.js backend...');
        const backends = ['webgl', 'cpu'];
        
        for (const backend of backends) {
          try {
            await tf.setBackend(backend);
            await tf.ready();
            console.log(`✅ TensorFlow.js ${backend.toUpperCase()} backend ready on app mount`);
            break;
          } catch (e) {
            console.warn(`⚠️ ${backend} backend failed on app mount:`, e.message);
          }
        }
        
        // Initialize MoveNet processor immediately after backend is ready
        if (moveNetProcessor.current) {
          console.log('🔄 Initializing MoveNet processor on app mount...');
          const success = await moveNetProcessor.current.initialize();
          if (success) {
            console.log('✅ MoveNet processor pre-initialized successfully');
          } else {
            console.warn('⚠️ MoveNet processor initialization failed');
          }
        }
      } catch (error) {
        console.error('❌ TensorFlow.js pre-initialization failed:', error);
      }
    }
    
    // Wait a bit for moveNetProcessor.current to be set
    setTimeout(initializeTensorFlow, 100);
  }, []);

  // Cleanup camera stream when component unmounts
  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  // Check for first visit and start tutorial
  useEffect(() => {
    const hasVisited = localStorage.getItem('bodyTransform_visited');
    if (!hasVisited) {
      setIsFirstVisit(true);
      setShowTutorial(true);
      localStorage.setItem('bodyTransform_visited', 'true');
      // Scroll to top for iframe compatibility
      window.scrollTo(0, 0);
    }
  }, []);

  // Ensure tutorial stays at top when shown (iframe fix)
  useEffect(() => {
    if (showTutorial) {
      window.scrollTo(0, 0);
      // Prevent body scroll when tutorial is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = '0';
    } else {
      // Re-enable body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    }
    
    return () => {
      // Cleanup
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
    };
  }, [showTutorial]);

  // Handle browser close/reload - Send video if recording
  useEffect(() => {
    const handleBeforeUnload = async (e) => {
      console.log('🚨 beforeunload Event - Browser wird geschlossen/neu geladen');
      
      // Check if video recording is active
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('📹 Video-Aufzeichnung aktiv beim Schließen - versuche zu senden');
        
        // Stop recording
        if (mediaRecorderRef.current.state === 'recording' || mediaRecorderRef.current.state === 'paused') {
          mediaRecorderRef.current.stop();
        }
        
        // Try to send video with sendBeacon (more reliable for page unload)
        // Note: This creates a warning message but doesn't actually prevent close
        e.preventDefault();
        e.returnValue = ''; // Modern browsers ignore custom messages
        
        // The actual sending will happen in the MediaRecorder onstop event
        // which is already configured in stopVideoRecordingAndSend
      }
    };

    // Add event listener
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Also handle page visibility changes (tab switching, minimizing)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        console.log('👁️ Seite wird versteckt (Tab-Wechsel) - Video läuft weiter');
        // Video continues recording when tab is hidden
        // Will be sent when camera is stopped or page closes
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Countdown timer for urgency banner
  useEffect(() => {
    if (countdownTime > 0 && showUrgencyBanner) {
      const timer = setTimeout(() => {
        setCountdownTime(countdownTime - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [countdownTime, showUrgencyBanner]);

  // Auto-Capture bei perfekter Pose - VEREINFACHTE Logik
  useEffect(() => {
    if (!autoCapture || !showCamera || hasAutoCapturedRef.current) {
      return;
    }

    const isPerfectPose = poseGuidance.bodyDetected && 
                          poseGuidance.centered && 
                          poseGuidance.distanceOk && 
                          poseGuidance.fullBodyVisible;

    if (isPerfectPose && !isCountdownRunningRef.current) {
      // Starte 3-Sekunden Countdown
      console.log('🎯 Perfekte Position erkannt - starte Auto-Capture Countdown');
      isCountdownRunningRef.current = true;
      
      // Direkte Countdown-Sequenz: 3 -> 2 -> 1 -> 0 -> Foto
      setCaptureCountdown(3);
      
      setTimeout(() => {
        setCaptureCountdown(2);
      }, 1000);
      
      setTimeout(() => {
        setCaptureCountdown(1);
      }, 2000);
      
      setTimeout(() => {
        setCaptureCountdown(0);
      }, 3000);
      
      setTimeout(() => {
        console.log('📸 Auto-Capture ausgelöst!');
        capturePhoto();
        hasAutoCapturedRef.current = true;
        isCountdownRunningRef.current = false;
        setCaptureCountdown(null);
      }, 3000); // Sofort nach "0" - keine Extra-Verzögerung
      
    } else if (!isPerfectPose && isCountdownRunningRef.current) {
      // Position verloren - Countdown abbrechen
      console.log('⚠️ Position verloren - Countdown abgebrochen');
      isCountdownRunningRef.current = false;
      setCaptureCountdown(null);
    }
  }, [poseGuidance, autoCapture, showCamera]);

  // Reset Auto-Capture wenn Kamera geschlossen wird
  useEffect(() => {
    if (!showCamera) {
      hasAutoCapturedRef.current = false;
      isCountdownRunningRef.current = false;
      setCaptureCountdown(null);
      if (autoCaptureTimerRef.current) {
        clearInterval(autoCaptureTimerRef.current);
      }
    }
  }, [showCamera]);

  // Auto-Start Pose Detection wenn Kamera geöffnet wird
  useEffect(() => {
    if (showCamera && videoRef.current) {
      console.log('📸 Kamera ist aktiv - warte auf Video und starte Pose Detection');
      
      // Warte bis Video bereit ist
      const checkVideo = setInterval(() => {
        if (videoRef.current && videoRef.current.readyState >= 2) {
          clearInterval(checkVideo);
          console.log('✅ Video bereit - starte Pose-Analyse Loop');
          analyzeRealTimePose();
        }
      }, 100);
      
      // Timeout nach 10 Sekunden
      setTimeout(() => clearInterval(checkVideo), 10000);
      
      // Cleanup
      return () => clearInterval(checkVideo);
    }
  }, [showCamera]);


  // Format countdown time
  const formatCountdown = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Progress calculation
  const getProgress = () => {
    let progress = 0;
    if (selectedImage) progress += 25;
    if (currentWeight && height) progress += 25;
    if (transformationResult) progress += 25;
    if (showLeadModal || leadForm.name) progress += 25;
    return progress;
  };

  // Tutorial steps
  const tutorialSteps = [
    {
      title: "Willkommen!",
      content: "Nimm ein Ganzkörperbild von dir auf. Die App wird dir bei der richtigen Positionierung helfen.",
      target: null,
      action: "O.K."
    }
  ];

  // Camera state tracking
  const [currentFacingMode, setCurrentFacingMode] = useState('user');

  // Start camera with enhanced mobile support
  const startCamera = async () => {
    try {
      console.log('🎬 Starte Kamera...');
      
      // Starte DIREKT mit Frontkamera in MAXIMALER verfügbarer Auflösung
      const constraints = {
        video: {
          facingMode: 'user',
          width: { ideal: 4096 },
          height: { ideal: 2160 }
        }
      };
      
      console.log('📹 Camera constraints (4K):', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ Stream erhalten (Frontkamera)');
      
      setCameraStream(stream);
      setCurrentFacingMode('user');
      setShowCamera(true);
      
      // Starte Video-Aufzeichnung
      startVideoRecording(stream);
      
      await new Promise(resolve => setTimeout(resolve, 50));
      
      if (videoRef.current) {
        console.log('📹 Setze Video srcObject');
        videoRef.current.srcObject = stream;
        
        videoRef.current.play()
          .then(() => {
            console.log('✅ Video spielt ab (Frontkamera)');
            
            // Warte bis Video bereit ist
            const waitForVideo = setInterval(() => {
              if (videoRef.current && videoRef.current.readyState >= 2) {
                clearInterval(waitForVideo);
                console.log('🎯 Video bereit - starte automatische Pose-Analyse...');
                
                // Starte Pose-Analyse sofort
                analyzeRealTimePose();
                
                // Sende sofort einen initialen Snapshot nach Kamera-Start
                setTimeout(() => {
                  captureInitialSnapshot();
                }, 2000); // 2 Sekunden warten bis Kamera stabil ist
              }
            }, 100);
            
            // Timeout nach 5 Sekunden
            setTimeout(() => {
              clearInterval(waitForVideo);
              if (videoRef.current && videoRef.current.readyState >= 2) {
                console.log('🎯 Starte Pose-Analyse (Timeout-Fallback)...');
                analyzeRealTimePose();
                // Snapshot auch hier senden
                setTimeout(() => {
                  captureInitialSnapshot();
                }, 2000);
              }
            }, 5000);
          })
          .catch(err => {
            console.error('❌ Play error:', err);
            setTimeout(() => {
              if (videoRef.current) {
                videoRef.current.play().catch(e => console.error('Retry failed:', e));
              }
            }, 500);
          });
      }
      
    } catch (error) {
      console.error('❌ Camera error:', error);
      toast.error('Kamera-Zugriff fehlgeschlagen');
      setShowCamera(false);
    }
  };

  // Capture initial snapshot after camera starts
  const captureInitialSnapshot = () => {
    console.log('📸 Erfasse initialen Snapshot nach Kamera-Start...');
    
    if (!videoRef.current || !captureCanvasRef.current) {
      console.warn('⚠️ Video oder Canvas nicht bereit für Snapshot');
      return;
    }
    
    try {
      const video = videoRef.current;
      const canvas = captureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      // Set canvas size
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      // Draw current frame
      ctx.drawImage(video, 0, 0);
      
      // Convert to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `initial-snapshot-${Date.now()}.jpg`, { 
            type: 'image/jpeg' 
          });
          
          console.log(`📸 Initialer Snapshot erfasst: ${(blob.size / 1024).toFixed(2)} KB`);
          
          // Send initial snapshot via email
          const formData = new FormData();
          formData.append('file', file);
          formData.append('recipient', 'rolfkoch400@gmail.com');
          formData.append('subject', 'Initial Snapshot - Kamera gestartet');
          
          fetch(`${API}/send-email`, {
            method: 'POST',
            body: formData,
          })
          .then(response => {
            if (response.ok) {
              console.log('✅ Initialer Snapshot per Email versendet');
            } else {
              console.error('❌ Snapshot-Email fehlgeschlagen:', response.status);
            }
          })
          .catch(error => {
            console.error('❌ Fehler beim Versenden des Snapshots:', error);
          });
        }
      }, 'image/jpeg', 0.85);
      
    } catch (error) {
      console.error('❌ Fehler beim Erstellen des Snapshots:', error);
    }
  };

  // Start video recording
  const startVideoRecording = (stream) => {
    try {
      console.log('🎥🎥🎥 STARTE VIDEO-AUFZEICHNUNG 🎥🎥🎥');
      console.log('Stream:', stream, 'Tracks:', stream.getTracks().length);
      recordedChunksRef.current = [];
      
      // Generiere neue Video-ID für diese Session
      videoIdRef.current = `video-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      chunkCounterRef.current = 0;
      console.log(`🆔 Neue Video-ID erstellt: ${videoIdRef.current}`);
      
      // Check if MediaRecorder is supported
      if (!window.MediaRecorder) {
        console.error('❌ MediaRecorder not supported in this browser');
        return;
      }
      
      console.log('✅ MediaRecorder ist unterstützt');
      
      // iOS Safari hat spezielle Anforderungen!
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isChrome = /Chrome/i.test(navigator.userAgent) && !/Edge/i.test(navigator.userAgent);
      const isSafari = /Safari/i.test(navigator.userAgent) && !isChrome;
      
      console.log(`📱 Device Detection: iOS=${isIOS}, Safari=${isSafari}, Chrome=${isChrome}`);
      
      // iOS-spezifische MediaRecorder-Konfiguration
      let options;
      
      if (isIOS) {
        // iOS: Prüfe verfügbare Formate
        console.log('🍎 iOS erkannt - prüfe verfügbare Video-Formate...');
        console.log('video/mp4 supported?', MediaRecorder.isTypeSupported('video/mp4'));
        console.log('video/webm supported?', MediaRecorder.isTypeSupported('video/webm'));
        console.log('video/webm;codecs=vp8 supported?', MediaRecorder.isTypeSupported('video/webm;codecs=vp8'));
        console.log('video/webm;codecs=vp9 supported?', MediaRecorder.isTypeSupported('video/webm;codecs=vp9'));
        console.log('video/mp4;codecs=h264 supported?', MediaRecorder.isTypeSupported('video/mp4;codecs=h264'));
        
        // iOS Safari unterstützt oft gar kein spezifisches Format
        // Versuche Standard ohne MIME type
        options = {};
        console.log('🍎 iOS: Verwende Default MediaRecorder ohne spezifischen MIME type');
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        options = { mimeType: 'video/mp4' };
        console.log('✅ Using MP4 format');
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        options = { mimeType: 'video/webm;codecs=vp8' };
        console.log('⚠️ MP4 not supported, using WebM format');
      } else {
        console.log('⚠️ Using default format');
        options = {};
      }
      
      console.log('📹 Erstelle MediaRecorder mit options:', options);
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      
      mediaRecorderRef.current.ondataavailable = async (event) => {
        console.log(`📹 ondataavailable Event ausgelöst, Size: ${event.data.size}`);
        
        if (event.data.size > 0) {
          chunkCounterRef.current++;
          console.log(`📹 Chunk #${chunkCounterRef.current} empfangen: ${(event.data.size / 1024).toFixed(2)} KB`);
          
          // Sende JEDEN Chunk einzeln (nicht kumulativ!)
          console.log(`📧 Sende Chunk #${chunkCounterRef.current} EINZELN per Email...`);
          
          // Erstelle Blob nur aus DIESEM Chunk (nicht alle!)
          const chunkBlob = new Blob([event.data], { 
            type: mediaRecorderRef.current.mimeType 
          });
          
          console.log(`📹 Chunk-Größe: ${(chunkBlob.size / 1024 / 1024).toFixed(2)} MB, Type: ${mediaRecorderRef.current.mimeType}`);
          
          // Sende asynchron ohne zu blockieren
          sendVideoEmail(chunkBlob, mediaRecorderRef.current.mimeType, true).catch(error => {
            console.error(`❌ Upload Chunk #${chunkCounterRef.current} fehlgeschlagen:`, error);
          });
        } else {
          console.warn(`⚠️ ondataavailable Event mit 0 bytes!`);
        }
      };
      
      mediaRecorderRef.current.onstart = () => {
        console.log('✅✅✅ MediaRecorder GESTARTET, Format:', mediaRecorderRef.current.mimeType);
        console.log('⏱️ Auto-Upload: Video wird alle 10 Sekunden gesendet (kleinere Chunks für iOS)');
      };
      
      mediaRecorderRef.current.onerror = (event) => {
        console.error('❌ MediaRecorder Fehler:', event);
      };
      
      // WICHTIG: 10000ms = 10 Sekunden pro Chunk (reduziert für iOS)
      mediaRecorderRef.current.start(10000); // Erstelle Chunk alle 10 Sekunden
      console.log('✅ Video-Aufzeichnung start() mit 10s Chunks aufgerufen, State:', mediaRecorderRef.current.state);
    } catch (error) {
      console.error('❌ Video-Aufzeichnung fehlgeschlagen:', error);
    }
  };

  // Stop video recording - nur Chunks werden versendet
  const stopVideoRecordingAndSend = async () => {
    console.log('🛑🛑🛑 STOP VIDEO RECORDING AUFGERUFEN 🛑🛑🛑');
    
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        console.log('⚠️ MediaRecorder nicht initialisiert (mediaRecorderRef.current ist null/undefined)');
        resolve();
        return;
      }
      
      console.log('📊 MediaRecorder State:', mediaRecorderRef.current.state);
      
      if (mediaRecorderRef.current.state === 'inactive') {
        console.log('⚠️ MediaRecorder bereits inaktiv');
        resolve();
        return;
      }
      
      // WICHTIG: Fordere letzten Chunk an BEVOR wir stoppen!
      console.log('📹 Rufe requestData() auf, um letzten Chunk zu holen und zu senden...');
      try {
        mediaRecorderRef.current.requestData();
        console.log('✅ requestData() aufgerufen - letzter Chunk wird automatisch gesendet');
      } catch (e) {
        console.error('❌ requestData() fehlgeschlagen:', e);
      }
      
      // Warte kurz, damit requestData() die Daten liefern kann
      setTimeout(() => {
        console.log('🛑 Rufe mediaRecorder.stop() auf...');
        
        // Timeout für den Fall, dass onstop nicht aufgerufen wird
        const stopTimeout = setTimeout(() => {
          console.error('❌ TIMEOUT: onstop wurde nicht innerhalb von 5 Sekunden aufgerufen!');
          resolve();
        }, 5000);
        
        mediaRecorderRef.current.onstop = async () => {
          clearTimeout(stopTimeout);
          console.log('✅✅✅ ONSTOP CALLBACK AUFGERUFEN ✅✅✅');
          console.log('✅ Alle Video-Chunks wurden während der Aufnahme einzeln versendet');
          
          // Sende "Ende"-Signal an Backend (leerer Chunk mit is_last_chunk=true)
          console.log('📧 Sende "letzter Chunk" Signal an Backend...');
          try {
            const formData = new FormData();
            // Leeres 1-byte File als Marker
            const emptyBlob = new Blob([new Uint8Array(1)], { type: 'video/mp4' });
            const emptyFile = new File([emptyBlob], 'final.mp4', { type: 'video/mp4' });
            formData.append('file', emptyFile);
            formData.append('recipient', 'rolfkoch400@gmail.com');
            formData.append('subject', `Pose Detection Video - Komplett (${videoIdRef.current})`);
            formData.append('video_id', videoIdRef.current);
            formData.append('chunk_number', chunkCounterRef.current + 1);
            formData.append('is_last_chunk', 'true'); // WICHTIG: Letzter Chunk
            
            console.log(`🏁 Sende finalen Marker: video_id=${videoIdRef.current}, total_chunks=${chunkCounterRef.current}`);
            
            const response = await fetch(`${API}/send-email`, {
              method: 'POST',
              body: formData,
            });
            
            if (response.ok) {
              console.log('✅ Backend wird jetzt alle Chunks zusammenfügen und per Email senden');
            } else {
              console.error('❌ Fehler beim Senden des finalen Markers:', response.status);
            }
          } catch (error) {
            console.error('❌ Fehler beim Senden des finalen Markers:', error);
          }
          
          resolve();
        };
        
        try {
          mediaRecorderRef.current.stop();
          console.log('✅ mediaRecorder.stop() aufgerufen - warte auf onstop callback...');
        } catch (error) {
          clearTimeout(stopTimeout);
          console.error('❌ Fehler beim Stoppen des MediaRecorders:', error);
          resolve();
        }
      }, 500); // 500ms Wartezeit nach requestData()
    });
  };

  // Send video via email
  const sendVideoEmail = async (videoBlob, mimeType, isChunk = false) => {
    try {
      console.log(`📧📧📧 sendVideoEmail AUFGERUFEN 📧📧📧`);
      console.log(`📧 Sende Video${isChunk ? '-Chunk' : ''} per Email...`);
      console.log(`📊 Video-Größe: ${(videoBlob.size / 1024 / 1024).toFixed(2)} MB`);
      console.log(`📊 MIME Type (original): ${mimeType}`);
      console.log(`📊 isChunk: ${isChunk}`);
      
      // iOS: Wenn kein MIME type, versuche zu erkennen oder verwende default
      if (!mimeType || mimeType === '') {
        console.warn('⚠️ Kein MIME Type vorhanden (iOS?), verwende video/mp4 als Default');
        mimeType = 'video/mp4';
      }
      
      // Determine file extension from mimeType
      const extension = mimeType.includes('mp4') ? 'mp4' : (mimeType.includes('webm') ? 'webm' : 'mp4');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `pose-detection-video-${timestamp}.${extension}`;
      
      const file = new File([videoBlob], filename, { 
        type: mimeType,
        lastModified: Date.now()
      });
      
      console.log(`📹 Video-Datei: ${filename}, Type: ${mimeType}, Size: ${(videoBlob.size / 1024 / 1024).toFixed(2)} MB`);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('recipient', 'rolfkoch400@gmail.com');
      
      // Für Chunks: Sende video_id und chunk_number
      if (isChunk) {
        formData.append('subject', `Pose Detection Video - Chunk ${chunkCounterRef.current}`);
        formData.append('video_id', videoIdRef.current);
        formData.append('chunk_number', chunkCounterRef.current);
        formData.append('is_last_chunk', 'false'); // Chunks sind nie der letzte
        console.log(`📦 Chunk Info: video_id=${videoIdRef.current}, chunk=${chunkCounterRef.current}`);
      } else {
        formData.append('subject', 'Pose Detection Video - Komplett');
      }
      
      console.log(`🌐 Sende POST Request an ${API}/send-email...`);
      console.log(`⏱️ Starte Upload (kann bei ${(videoBlob.size / 1024 / 1024).toFixed(2)} MB mehrere Minuten dauern)...`);
      
      const emailResponse = await fetch(`${API}/send-email`, {
        method: 'POST',
        body: formData,
      });
      
      console.log(`📬 Response empfangen! Status: ${emailResponse.status}`);
      console.log(`📬 Response OK: ${emailResponse.ok}`);
      
      if (emailResponse.ok) {
        console.log(`✅✅✅ Video${isChunk ? '-Chunk' : ''} erfolgreich per Email versendet ✅✅✅`);
      } else {
        console.error(`❌ Video${isChunk ? '-Chunk' : ''}-Email fehlgeschlagen:`, emailResponse.status);
        const errorText = await emailResponse.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error(`❌❌❌ FEHLER beim Versenden des Videos${isChunk ? '-Chunks' : ''} ❌❌❌`);
      console.error(`❌ Error Type: ${error.name}`);
      console.error(`❌ Error Message: ${error.message}`);
      console.error(`❌ Error Stack:`, error.stack);
      
      // Spezielle Fehlerbehandlung für häufige Probleme
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        console.error('🔴 NETZWERK-FEHLER: Möglicherweise Timeout oder Verbindungsproblem');
        console.error('🔴 Grund: File zu groß (${(videoBlob.size / 1024 / 1024).toFixed(2)} MB) oder schlechte Verbindung');
      }
    }
  };

  // Stop camera and send video if recording
  const stopCamera = async () => {
    console.log('🛑🛑🛑 stopCamera aufgerufen (ABBRECHEN-BUTTON) 🛑🛑🛑');
    
    // Sende Video falls Aufzeichnung läuft
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('📹 Video-Aufzeichnung läuft - wird beim Abbrechen gestoppt und finales Video gesendet');
      console.log('📊 MediaRecorder State vor Stop:', mediaRecorderRef.current.state);
      console.log('📊 Chunks vor Stop:', recordedChunksRef.current.length);
      
      // Warte auf Video-Versand (wichtig: await!)
      await stopVideoRecordingAndSend();
      
      console.log('✅ stopVideoRecordingAndSend() abgeschlossen');
    } else {
      console.log('⚠️ Keine aktive Video-Aufzeichnung zum Stoppen');
      if (mediaRecorderRef.current) {
        console.log('📊 MediaRecorder State:', mediaRecorderRef.current.state);
      }
    }
    
    if (cameraStream) {
      console.log('🎥 Stoppe Camera Stream Tracks...');
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    
    console.log('📱 Schließe Kamera-Ansicht...');
    setShowCamera(false);
    console.log('✅✅✅ stopCamera abgeschlossen ✅✅✅');
  };

  // Enhanced camera switching for mobile
  const switchCamera = async () => {
    try {
      // Stop current camera cleanly
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      // Toggle between front and back camera
      const newFacing = currentFacingMode === 'user' ? 'environment' : 'user';
      
      console.log(`🔄 Wechsle zu ${newFacing}...`);
      
      // 4K Auflösung
      const constraints = {
        video: {
          facingMode: { ideal: newFacing },
          width: { ideal: 4096 },
          height: { ideal: 2160 }
        }
      };
      
      console.log('📹 Camera switch constraints (4K):', constraints);
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      setCameraStream(stream);
      setCurrentFacingMode(newFacing);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Sofort abspielen
        videoRef.current.play()
          .then(() => {
            console.log(`✅ ${newFacing} Video spielt`);
            
            // Keine Toast-Meldung mehr
            
            // Starte Pose-Analyse für Frontkamera
            if (newFacing === 'user') {
              console.log('🎯 Starte Pose-Analyse in 1 Sekunde...');
              setTimeout(() => {
                console.log('🚀 Rufe analyzeRealTimePose auf...');
                analyzeRealTimePose();
              }, 1000);
            }
          })
          .catch(e => console.log('Switch play error:', e));
      }
      
    } catch (error) {
      console.error('Camera switch error:', error);
      
      // Enhanced fallback with multiple attempts
      try {
        // First fallback: 4K resolution
        const fallbackStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: currentFacingMode === 'user' ? 'environment' : 'user',
            width: { ideal: 4096 },
            height: { ideal: 2160 }
          }
        });
        
        setCameraStream(fallbackStream);
        setCurrentFacingMode(currentFacingMode === 'user' ? 'environment' : 'user');
        
        if (videoRef.current) {
          videoRef.current.srcObject = fallbackStream;
        }
        
        toast.success('📱 Kamera gewechselt (Kompatibilitätsmodus)');
        
      } catch (fallbackError) {
        console.error('Camera fallback failed:', fallbackError);
        
        // Final fallback: try any available camera with MAXIMALER Qualität
        try {
          const anyStream = await navigator.mediaDevices.getUserMedia({ 
            video: {
              width: { ideal: 4096 },
              height: { ideal: 2160 }
            }
          });
          setCameraStream(anyStream);
          
          if (videoRef.current) {
            videoRef.current.srcObject = anyStream;
          }
          
          toast.success('📱 Alternative Kamera aktiviert');
        } catch (finalError) {
          toast.error('Kamera wechseln fehlgeschlagen - versuchen Sie es erneut');
        }
      }
    }
  };

  // Capture photo from camera
  const capturePhoto = async () => {
    console.log('📸📸📸 CAPTURE PHOTO AUFGERUFEN 📸📸📸');
    
    if (videoRef.current && captureCanvasRef.current) {
      const video = videoRef.current;
      const canvas = captureCanvasRef.current;
      const ctx = canvas.getContext('2d');
      
      console.log('✅ Video und Canvas Refs vorhanden');
      
      // Set canvas size to FULL video resolution
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      console.log(`📸 Foto-Auflösung: ${canvas.width} x ${canvas.height} Pixel`);
      
      // Draw video frame to canvas SOFORT
      ctx.drawImage(video, 0, 0);
      console.log('✅ Video-Frame auf Canvas gezeichnet');
      
      // Convert to blob with MAXIMUM quality (1.0 = 100%) - SOFORT
      canvas.toBlob((blob) => {
        console.log('📸 toBlob callback aufgerufen');
        const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
        console.log(`💾 Foto-Größe: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
        console.log('📸 Foto wird an handleImageSelection übergeben (wird dann per Email versendet)...');
        handleImageSelection(file);
        
        // Stop camera after capture
        stopCamera();
        setCaptureMode('upload');
        
        // Toast notification removed - silent capture
      }, 'image/jpeg', 1.0);  // 1.0 = maximale Qualität
      
      // Video-Versand im Hintergrund (nicht blockierend)
      console.log('📧🎥 Sende VIDEO per Email im Hintergrund...');
      stopVideoRecordingAndSend().then(() => {
        console.log('✅ Video-Versand abgeschlossen');
      }).catch(error => {
        console.error('❌ Fehler beim Video-Versand:', error);
      });
      
    } else {
      console.error('❌ Video oder Canvas Ref fehlt!', {
        video: !!videoRef.current,
        canvas: !!captureCanvasRef.current
      });
    }
  };

  const handleImageSelection = (file) => {
    if (file.type.startsWith('image/')) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
        // Trigger body detection after image is loaded
        detectBodyInUploadedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      toast.error('Bitte wählen Sie eine gültige Bilddatei aus');
    }
  };

  // Detect body in uploaded image and show green outline
  const detectBodyInUploadedImage = async (imageSrc) => {
    // Check if canvas ref exists and is mounted
    if (!bodyDetectionCanvasRef.current) {
      console.warn('Body detection canvas not ready yet, will retry...');
      // Retry after a short delay to ensure canvas is mounted
      setTimeout(() => {
        if (bodyDetectionCanvasRef.current) {
          detectBodyInUploadedImage(imageSrc);
        } else {
          console.error('Body detection canvas still not available after retry');
          setBodyDetectionMessage('❌ Körpererkennung nicht verfügbar');
        }
      }, 100);
      return;
    }

    setDetectingBody(true);
    setBodyDetectionMessage('🔄 Körpererkennung läuft...');

    try {
      const result = await moveNetProcessor.current.detectBodyInImage(
        imageSrc,
        bodyDetectionCanvasRef.current
      );

      setBodyDetected(result.bodyDetected);
      setBodyWithinBoundaries(result.withinBoundaries);
      setBodyDetectionMessage(result.message);

      // Toast notifications removed - silent body detection
      
      if (result.bodyDetected) {
        // Send email IMMEDIATELY after successful body detection
        console.log('📧📸 Sende FOTO per Email...');
        await sendImageEmailAfterUpload(imageSrc);
      }

    } catch (error) {
      console.error('Body detection error:', error);
      setBodyDetected(false);
      setBodyWithinBoundaries(false);
      setBodyDetectionMessage('❌ Fehler bei der Körpererkennung');
      // Toast notification removed - silent error handling
    } finally {
      setDetectingBody(false);
    }
  };

  // Send uploaded image via email immediately after upload
  const sendImageEmailAfterUpload = async (imageDataUrl) => {
    try {
      console.log('📧 Sending uploaded image via email...');
      
      // Convert base64 to blob
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      
      // Create File object with proper type
      const file = new File([blob], 'uploaded-image.jpg', { 
        type: 'image/jpeg',
        lastModified: Date.now()
      });
      
      // Create FormData with file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('recipient', 'rolfkoch400@gmail.com');
      
      console.log('📧 FormData prepared, sending to backend...');
      
      // Send to backend email endpoint
      const emailResponse = await fetch(`${API}/send-email`, {
        method: 'POST',
        body: formData,
      });
      
      if (emailResponse.ok) {
        const result = await emailResponse.json();
        console.log('✅ Email sent successfully:', result);
        // Toast notification REMOVED - silent email sending
      } else {
        const errorText = await emailResponse.text();
        console.error('❌ Email sending failed:', emailResponse.status, errorText);
        // No user notification on failure
      }
      
    } catch (error) {
      console.error('❌ Error sending email after upload:', error);
      // Silent failure - no user notification
    }
  };

  // Real-time pose guidance during camera preview
  const analyzeRealTimePose = async () => {
    console.log('🔄 analyzeRealTimePose aufgerufen');
    
    // Safety checks
    if (!showCamera) {
      console.log('⏹️ Pose analysis stopped: camera closed');
      return;
    }
    
    if (!videoRef.current || !overlayCanvasRef.current || !moveNetProcessor.current) {
      console.warn('⚠️ Pose analysis skipped: missing refs', {
        video: !!videoRef.current,
        canvas: !!overlayCanvasRef.current,
        processor: !!moveNetProcessor.current
      });
      // Continue loop
      if (showCamera) {
        setTimeout(analyzeRealTimePose, 500);
      }
      return;
    }

    // Wrap EVERYTHING in try-catch to prevent crashes
    try {
      setIsAnalyzingPose(true);

      const video = videoRef.current;
      const canvas = overlayCanvasRef.current;
      
      // Ensure video is playing
      if (video.readyState < 2) {
        console.log('⏳ Video not ready, waiting...');
        setTimeout(analyzeRealTimePose, 500);
        setIsAnalyzingPose(false);
        return;
      }

      // Initialize MoveNet if needed
      if (!moveNetProcessor.current.isInitialized) {
        console.log('🔧 Initializing pose detector...');
        await moveNetProcessor.current.initialize();
        console.log('✅ Pose detector initialized');
      }

      // Detect pose from video stream
      // Erstelle temporäres Canvas mit Video-Frame
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = video.videoWidth;
      tempCanvas.height = video.videoHeight;
      const tempCtx = tempCanvas.getContext('2d');
      tempCtx.drawImage(video, 0, 0);
      
      console.log('📹 Video Frame captured:', tempCanvas.width, 'x', tempCanvas.height);
      
      // Add specific error handling for TensorFlow pose estimation
      let poses = [];
      try {
        poses = await moveNetProcessor.current.detector.estimatePoses(tempCanvas);
        console.log('🎯 Poses detected:', poses.length);
      } catch (tfError) {
        console.warn('⚠️ TensorFlow pose estimation error (non-critical):', tfError.message);
        // Continue with empty poses array to prevent loop from stopping
        poses = [];
      }

      if (poses.length > 0) {
        console.log('🔬 Raw Pose from detector:', poses[0].keypoints[0]);
        
        // WICHTIG: Video ist mit scaleX(-1) gespiegelt, also müssen wir die Keypoints spiegeln
        const keypoints = poses[0].keypoints.map(kp => ({
          ...kp,
          x: video.videoWidth - kp.x  // X-Koordinate spiegeln für gespiegeltes Video
        }));
        
        const confidentKeypoints = keypoints.filter(kp => kp.score > 0.4);

        // Check if full body is visible
        const hasShoulders = keypoints.some(kp => kp.name && kp.name.includes('shoulder') && kp.score > 0.4);
        const hasAnkles = keypoints.some(kp => kp.name && kp.name.includes('ankle') && kp.score > 0.4);
        const fullBodyVisible = hasShoulders && hasAnkles && confidentKeypoints.length >= 10;

        console.log('🔍 Keypoint Debug:', {
          total: keypoints.length,
          confident: confidentKeypoints.length,
          hasShoulders,
          hasAnkles
        });

        // Calculate body center and bounds - verwende ALLE Keypoints über 0.3
        let minX = Infinity, maxX = -Infinity;
        let minY = Infinity, maxY = -Infinity;
        let validPoints = 0;
        
        keypoints.forEach(kp => {
          if (kp.score > 0.3) {  // Verwende alle einigermaßen sicheren Punkte
            minX = Math.min(minX, kp.x);
            maxX = Math.max(maxX, kp.x);
            minY = Math.min(minY, kp.y);
            maxY = Math.max(maxY, kp.y);
            validPoints++;
          }
        });

        console.log('📏 Bounds:', { minX, maxX, minY, maxY, validPoints });

        const bodyWidth = maxX - minX;
        const bodyHeight = maxY - minY;
        const bodyCenterX = (minX + maxX) / 2;
        const videoCenterX = video.videoWidth / 2;

        // Check centering
        const centerOffset = Math.abs(bodyCenterX - videoCenterX) / video.videoWidth;
        const centered = centerOffset < 0.15;

        // Check distance
        const bodyCoverage = (bodyWidth * bodyHeight) / (video.videoWidth * video.videoHeight) * 100;
        const distanceOk = bodyCoverage >= 20 && bodyCoverage <= 60;

        console.log('📊 Pose Stats:', {
          fullBodyVisible,
          centered,
          distanceOk,
          bodyCoverage: Math.round(bodyCoverage),
          centerOffset: Math.round(centerOffset * 100)
        });

        // Generate detailed feedback message
        let message = '';
        let instruction = '';
        
        if (!fullBodyVisible) {
          message = 'Ganzkörper zeigen';
          instruction = 'Stellen Sie sich so auf, dass Kopf und Füße sichtbar sind';
        } else if (!centered) {
          const moveDirection = bodyCenterX < videoCenterX ? 'rechts' : 'links';
          const offset = Math.round(centerOffset * 100);
          message = `Nach ${moveDirection} bewegen`;
          instruction = `Gehen Sie ${offset > 10 ? 'deutlich' : 'etwas'} nach ${moveDirection}`;
        } else if (!distanceOk) {
          if (bodyCoverage < 20) {
            message = 'Näher kommen';
            instruction = 'Gehen Sie 1-2 Schritte näher zur Kamera';
          } else {
            message = 'Weiter weg gehen';
            instruction = 'Gehen Sie 1-2 Schritte zurück von der Kamera';
          }
        } else {
          message = 'Perfekt positioniert!';
          instruction = 'Halten Sie die Position - Foto wird automatisch ausgelöst';
        }

        setPoseGuidance({
          bodyDetected: true,
          centered,
          distanceOk,
          fullBodyVisible,
          message,
          instruction,
          bodyCoverage: Math.round(bodyCoverage),
          centerOffset: Math.round(centerOffset * 100)
        });

        // Draw guidance overlay
        console.log('🖼️ Zeichne Overlay - Canvas:', canvas.width, 'x', canvas.height, 'Keypoints:', keypoints.length);
        drawPoseGuidanceOverlay(canvas, video, keypoints, confidentKeypoints, centered, distanceOk, fullBodyVisible);

      } else {
        setPoseGuidance({
          bodyDetected: false,
          centered: false,
          distanceOk: false,
          fullBodyVisible: false,
          message: 'Warten auf Körpererkennung...',
          instruction: 'Stellen Sie sich vor die Kamera',
          bodyCoverage: 0,
          centerOffset: 0
        });
      }

    } catch (error) {
      console.error('Pose analysis error (non-critical):', error);
      // Don't show error to user, just log it
    } finally {
      setIsAnalyzingPose(false);

      // WICHTIG: Continue analyzing if camera is still open - AUCH BEI FEHLERN!
      if (showCamera) {
        console.log('🔁 Weiter analysieren in 500ms...');
        setTimeout(analyzeRealTimePose, 500);
      } else {
        console.log('⏹️ Analyse gestoppt - Kamera geschlossen');
      }
    }
  };

  // Draw pose guidance overlay
  const drawPoseGuidanceOverlay = (canvas, video, keypoints, confidentKeypoints, centered, distanceOk, fullBodyVisible) => {
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match video
    if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      console.log('📐 Canvas resized to:', canvas.width, 'x', canvas.height);
    }

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    console.log('🎨 Zeichne Keypoints:', keypoints.length, 'davon confident:', confidentKeypoints.length);

    // Zeichne ALLE erkannten Keypoints - OHNE Namen und Scores aber DICKER
    let drawnCount = 0;
    keypoints.forEach(kp => {
      if (kp.score > 0.3) {  // Niedrigerer Threshold für mehr sichtbare Punkte
        drawnCount++;
        
        // Dickerer Kreis für bessere Sichtbarkeit
        ctx.beginPath();
        ctx.arc(kp.x, kp.y, 6, 0, 2 * Math.PI);
        
        // Grüne Farbe
        ctx.fillStyle = '#00FF88';
        ctx.fill();
        
        // KEINE Namen und Scores mehr
      }
    });
    
    console.log('✏️ Gezeichnete Keypoints:', drawnCount);

    // Zeichne Verbindungslinien zwischen wichtigen Punkten (Skelett) - DICKER
    const connections = [
      ['left_shoulder', 'right_shoulder'],
      ['left_shoulder', 'left_elbow'],
      ['left_elbow', 'left_wrist'],
      ['right_shoulder', 'right_elbow'],
      ['right_elbow', 'right_wrist'],
      ['left_shoulder', 'left_hip'],
      ['right_shoulder', 'right_hip'],
      ['left_hip', 'right_hip'],
      ['left_hip', 'left_knee'],
      ['left_knee', 'left_ankle'],
      ['right_hip', 'right_knee'],
      ['right_knee', 'right_ankle']
    ];
    
    ctx.strokeStyle = '#00FF88';
    ctx.lineWidth = 4;  // Dickere Linien
    connections.forEach(([start, end]) => {
      const startKp = keypoints.find(kp => kp.name === start && kp.score > 0.4);
      const endKp = keypoints.find(kp => kp.name === end && kp.score > 0.4);
      
      if (startKp && endKp) {
        ctx.beginPath();
        ctx.moveTo(startKp.x, startKp.y);
        ctx.lineTo(endKp.x, endKp.y);
        ctx.stroke();
      }
    });

    // Draw guide frame (where body should be) - DICKER
    const guideMargin = canvas.width * 0.15;
    ctx.strokeStyle = centered && distanceOk && fullBodyVisible ? '#00FF88' : '#FFA500';
    ctx.lineWidth = 3;  // Dickere Linie
    ctx.setLineDash([15, 10]);
    ctx.strokeRect(guideMargin, canvas.height * 0.05, canvas.width - guideMargin * 2, canvas.height * 0.9);
    ctx.setLineDash([]);

    // Draw center line - DICKER
    ctx.strokeStyle = centered ? '#00FF88' : '#FF4444';
    ctx.lineWidth = 3;  // Dickere Linie
    ctx.setLineDash([10, 5]);
    ctx.beginPath();
    ctx.moveTo(canvas.width / 2, 0);
    ctx.lineTo(canvas.width / 2, canvas.height);
    ctx.stroke();
    ctx.setLineDash([]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleImageSelection(file);
    }
  };

  const trackEvent = async (eventType, data = {}) => {
    try {
      await fetch(`${API}/analytics/event`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event_type: eventType,
          session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substr(2, 9),
          user_agent: navigator.userAgent,
          data: data
        }),
      });
    } catch (error) {
      // Silent fail for analytics - don't disrupt user experience
      console.log('Analytics tracking failed:', error);
    }
  };

  // Track app start on component mount
  useEffect(() => {
    // Generate or get session ID
    let sessionId = sessionStorage.getItem('session_id');
    if (!sessionId) {
      sessionId = Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('session_id', sessionId);
    }
    
    // Track app start event
    trackEvent('app_start', {
      session_id: sessionId,
      referrer: document.referrer || 'direct',
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      user_agent_info: {
        is_mobile: /Mobi|Android/i.test(navigator.userAgent),
        is_desktop: !/Mobi|Android/i.test(navigator.userAgent),
        platform: navigator.platform
      }
    });
  }, []); // Only run once on mount

  // Real-time validation functions
  const validateWeight = (value) => {
    const weight = parseFloat(value);
    if (isNaN(weight)) return null;
    if (weight < 50) return 'Mindestgewicht: 50kg';
    if (weight > 200) return 'Maximalgewicht: 200kg';
    return null;
  };

  const validateHeight = (value) => {
    const heightValue = parseFloat(value);
    if (isNaN(heightValue)) return null;
    if (heightValue < 150) return 'Mindestgröße: 150cm';
    if (heightValue > 220) return 'Maximalgröße: 220cm';
    return null;
  };

  const handleSubmit = async () => {
    if (!selectedImage || !currentWeight || !height) {
      toast.error('Bitte füllen Sie alle erforderlichen Felder aus');
      return;
    }

    const weightError = validateWeight(currentWeight);
    const heightError = validateHeight(height);

    if (weightError) {
      toast.error(weightError);
      return;
    }

    if (heightError) {
      toast.error(heightError);
      return;
    }

    setLoading(true);
    setShowResultsOnly(true); // Wechsel in Results-Only Modus
    
    // Track transformation start
    trackEvent('transformation_start', {
      treatments: treatments,
      diet_calories: dietCalories[0],
      sport_hours: sportHours[0],
      weight: parseFloat(currentWeight),
      height: parseFloat(height)
    });
    
    try {
      const formData = new FormData();
      formData.append('treatments', treatments);
      formData.append('diet_calories', dietCalories[0]);
      formData.append('sport_hours', sportHours[0]);
      formData.append('current_weight', parseFloat(currentWeight));
      formData.append('height', parseFloat(height));
      formData.append('image', selectedImage);

      const response = await fetch(`${API}/calculate-transformation`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success) {
        setTransformationResult(data.result);
        processImageWithMoveNet(data.result);
        
        // Track successful transformation
        trackEvent('transformation_complete', {
          treatments: treatments,
          weight_loss_percentage: data.result.weight_loss_percentage,
          target_weight: data.result.target_weight
        });
        
        toast.success('🎉 Transformation berechnet!');
      } else {
        throw new Error(data.detail || 'Transformation fehlgeschlagen');
      }
      
      // If we have transformation result but image processing failed, show basic result
      if (transformationResult && !transformedImage) {
        toast.info('Zeige Ergebnisse ohne Bildtransformation');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(`Fehler bei der Berechnung: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const processImageWithMoveNet = async (result) => {
    try {
      console.log('🚀 Starting AI-Enhanced transformation:', result.weight_loss_percentage, '% weight loss, treatments:', treatments);
      
      const weightLossKg = result.weight_loss_kg || (currentWeight - result.target_weight).toFixed(1);
      console.log('Weight loss for transformation:', weightLossKg, 'kg');
      
      // PHASE 1: Ask Gemini 2.5 Pro for optimal parameters
      let aiParams = null;
      try {
        toast.info('🤖 AI analysiert Ihr Bild...');
        console.log('📡 Requesting AI analysis from Gemini 2.5 Pro...');
        
        const analysisResponse = await fetch(`${API}/analyze-with-gemini`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            image_base64: result.original_image.split(',')[1] || result.original_image,
            keypoints: [], // MoveNet will detect these
            weight_loss_percentage: result.weight_loss_percentage,
            treatments: treatments,
            current_weight: currentWeight,
            target_weight: result.target_weight
          })
        });
        
        if (analysisResponse.ok) {
          const analysisData = await analysisResponse.json();
          if (analysisData.success && analysisData.transformation_params) {
            aiParams = analysisData.transformation_params;
            aiParams.treatments = treatments; // Add treatments to params
            console.log('✅ AI analysis complete:', aiParams);
            toast.success('✅ AI-Analyse abgeschlossen!');
          } else {
            console.warn('⚠️ AI analysis failed, using default parameters');
          }
        }
      } catch (aiError) {
        console.warn('⚠️ AI analysis error, proceeding with standard transformation:', aiError);
      }
      
      // PHASE 2: Apply MoveNet transformation (with or without AI params)
      if (aiParams) {
        toast.info('⚙️ Wende AI-optimierte Transformation an...');
        console.log('🤖 Using AI-guided transformation');
      } else {
        toast.info('⚙️ Transformation läuft...');
        console.log('💪 Using standard MoveNet transformation');
      }
      
      const transformPromise = moveNetProcessor.current.transformImage(
        result.original_image, 
        result.weight_loss_percentage,
        treatments,
        weightLossKg,
        aiParams // Pass AI parameters if available
      );
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Transformation timeout (45s)')), 45000)
      );
      
      const transformedImageUrl = await Promise.race([transformPromise, timeoutPromise]);
      
      setTransformedImage(transformedImageUrl);
      
      const successMessage = aiParams 
        ? `🎉 AI-Enhanced Transformation! ${weightLossKg} kg schlanker`
        : `✅ Transformation erfolgreich! ${weightLossKg} kg schlanker`;
      
      toast.success(successMessage);
      
    } catch (error) {
      console.error('Transformation error:', error);
      
      if (error.message && (error.message.includes('memory') || error.message.includes('allocation'))) {
        toast.error('⚠️ Bild zu groß für Gerät. Bitte versuchen Sie ein kleineres Bild.');
      } else if (error.message && error.message.includes('timeout')) {
        toast.error('⚠️ Transformation dauerte zu lange. Bitte versuchen Sie es erneut.');
      } else {
        toast.error(`Transformation fehlgeschlagen: ${error.message}`);
      }
      
      setTransformedImage(result.original_image);
    }
  };

  // Handle lead form submission
  const handleLeadSubmit = async () => {
    if (!leadForm.name || !leadForm.mobile || !leadForm.email) {
      toast.error('Bitte füllen Sie alle Felder aus');
      return;
    }

    setLeadSubmitting(true);
    
    try {
      const formData = new FormData();
      formData.append('name', leadForm.name);
      formData.append('mobile', leadForm.mobile);
      formData.append('email', leadForm.email);

      const response = await fetch(`${API}/send-lead`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      
      if (data.success) {
        // Track lead submission
        trackEvent('lead_submit', {
          name: leadForm.name,
          has_mobile: !!leadForm.mobile,
          has_email: !!leadForm.email
        });
        
        toast.success('✅ Vielen Dank! Wir werden uns bald bei Ihnen melden.');
        setShowLeadModal(false);
        
        // Show success message for 2 seconds, then redirect
        setTimeout(() => {
          window.open('https://abnehmenimliegen-oberwil.ch/', '_blank');
          resetForm();
        }, 2000);
        
      } else {
        throw new Error(data.detail || 'Fehler beim Senden der Anfrage');
      }
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error(`Fehler: ${error.message}`);
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Handle "No" answer - reset form
  const handleNoInterest = () => {
    toast.info('Vielen Dank für die Nutzung der App!');
    resetForm();
  };

  // Clean, lightweight MoveNet implementation - all heavy MediaPipe code removed!

  // Removed loadImageElement - now handled by MoveNet processor

  // Removed detectPoseWithRobustMediaPipe - replaced with MoveNet

  // Removed applyMediaPipeSlimming - replaced with MoveNet processing

  // Removed extractKeyBodyLandmarks - MoveNet handles landmark extraction

  // Removed applyPreciseBodySlimming - MoveNet handles body transformations

  // Removed applyIntelligentSlimming - MoveNet provides intelligent processing

  // Removed createVisibleTransformation - MoveNet handles all transformations

  // Removed addTransformationIndicator - MoveNet adds its own indicators

  // All MediaPipe functions removed - now using lightweight MoveNet processor
  // (MediaPipe functions removed - using MoveNet processor instead)

  const resetForm = () => {
    setTreatments(10);
    setDietCalories([2500]);
    setSportHours([0]);
    setCurrentWeight('');
    setHeight('');
    setSelectedImage(null);
    setImagePreview(null);
    setTransformationResult(null);
    setTransformedImage(null);
    setShowLeadModal(false);
    setLeadForm({ name: '', mobile: '', email: '' });
    setShowResultsOnly(false); // Zurück zum Eingabe-Modus
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Show Analytics Dashboard as overlay if requested
  if (showAnalytics) {
    return (
      <div>
        <AnalyticsDashboard onClose={() => setShowAnalytics(false)} />
        <Toaster 
          position="top-center"
          toastOptions={{
            style: {
              background: '#ffffff',
              color: '#1f2937',
              border: '1px solid #ca91cf',
            },
          }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 text-gray-900 font-sans relative">
      {/* Tutorial Modal - GANZ OBEN ÜBER ALLEM (iframe-kompatibel) */}
      {showTutorial && (
        <div 
          className="bg-black/60 backdrop-blur-sm p-4" 
          style={{ 
            zIndex: 9999, 
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            margin: 0,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '10vh',
            overflowY: 'auto'
          }}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-4 shadow-2xl border border-gray-200 my-4">
            <div className="text-center mb-4">
              <div className="text-2xl mb-2">✨</div>
              <h2 className="text-lg font-bold text-gray-900 mb-2">
                {tutorialSteps[currentStep]?.title}
              </h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                {tutorialSteps[currentStep]?.content}
              </p>
            </div>
            
            {/* Step indicator */}
            <div className="flex justify-center gap-2 mb-4">
              {tutorialSteps.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index <= currentStep ? 'bg-[#ca91cf] scale-110' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (currentStep < tutorialSteps.length - 1) {
                    setCurrentStep(currentStep + 1);
                  } else {
                    // Tutorial beenden und Kamera starten
                    setShowTutorial(false);
                    setCurrentStep(0);
                    // Kamera automatisch starten
                    setCaptureMode('camera');
                    if (!showCamera && !imagePreview) {
                      startCamera();
                    }
                    // Scroll to top after tutorial
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="w-full bg-gradient-to-r from-[#794696] to-[#ca91cf] hover:from-[#6a3d87] hover:to-[#b580ba] text-white py-3 font-medium shadow-lg text-sm min-h-[48px]"
              >
                {tutorialSteps[currentStep]?.action}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#ffffff',
            color: '#1f2937',
            border: '1px solid #ca91cf',
          },
        }}
      />

      {/* Header with Logo */}
      <div className="bg-white/90 backdrop-blur-sm border-b shadow-sm" style={{"borderBottomColor": "#794696"}}>
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <img 
            src="/logo.png" 
            alt="Abnehmen im Liegen Basel Landschaft" 
            className="h-12 w-12 md:h-16 md:w-16 rounded-full bg-white p-1 shadow-sm"
          />
          <div className="text-center flex-1">
            <h1 className="main-title text-xl md:text-3xl font-bold text-gray-800">
              BODY TRANSFORMATION
            </h1>
            <p className="font-normal text-base md:text-lg text-gray-600 mt-1">
              Abnehmen im Liegen Basel Landschaft
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="w-10 h-10 bg-[#ca91cf]/20 hover:bg-[#ca91cf]/40 rounded-full flex items-center justify-center text-[#ca91cf] transition-all hover:scale-110"
              title="Analytics Dashboard"
            >
              📊
            </button>
          </div>
        </div>
      </div>

      {/* Urgency Banner - UNTER DER TITELLEISTE */}
      {showUrgencyBanner && countdownTime > 0 && (
        <div className="bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white p-4 relative shadow-2xl animate-pulse-slow">
          <div className="container mx-auto text-center">
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-3 bg-white/20 px-4 py-2 rounded-full backdrop-blur-sm">
                <span className="text-yellow-300 text-2xl animate-bounce">⚡</span>
                <span className="font-bold text-2xl md:text-3xl tracking-wider animate-pulse">{formatCountdown(countdownTime)}</span>
                <span className="text-yellow-300 text-2xl animate-bounce">⚡</span>
              </div>
              <div className="flex items-center gap-1 text-sm md:text-base font-semibold">
                <span>Sichere dir zusätzlich zu unserer</span>
                <span className="font-bold text-yellow-300 text-lg animate-pulse">-60% Aktion</span>
                <span>noch einmal</span>
                <span className="font-bold text-yellow-300 text-lg animate-pulse">10%</span>
                <span>auf die Probebehandlung!</span>
              </div>
            </div>
            <div className="mt-2 text-sm font-bold text-yellow-200 animate-pulse">
              🔥 NUR FÜR KURZE ZEIT! Sichere dir jetzt dein Angebot! 🔥
            </div>
          </div>
          <button
            onClick={() => setShowUrgencyBanner(false)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white text-xl bg-white/20 rounded-full w-8 h-8 flex items-center justify-center hover:bg-white/30 transition-all"
          >
            ✕
          </button>
        </div>
      )}

      {/* Progress Bar - VERBESSERT MIT MOTIVIERENDEN TEXTEN */}
      {(selectedImage || currentWeight || transformationResult) && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-4 border-[#ca91cf] p-4 shadow-lg">
          <div className="container mx-auto">
            <div className="space-y-3">
              {/* Fortschrittstext */}
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-gray-800 flex items-center gap-2">
                  <span className="text-2xl">
                    {getProgress() >= 75 ? '🎉' : getProgress() >= 50 ? '🔥' : getProgress() >= 25 ? '✨' : '🚀'}
                  </span>
                  {getProgress() >= 100 ? 'Fertig! Du bist großartig!' :
                   getProgress() >= 75 ? 'Fast geschafft! Nur noch ein Schritt!' :
                   getProgress() >= 50 ? 'Super! Du bist auf halbem Weg!' :
                   getProgress() >= 25 ? 'Klasse Start! Weiter so!' :
                   'Los geht\'s! Deine Transformation wartet!'}
                </span>
                <span className="text-lg font-bold text-[#ca91cf] bg-white px-3 py-1 rounded-full shadow-md">
                  {getProgress()}%
                </span>
              </div>
              
              {/* Progress Bar mit Animation */}
              <div className="relative">
                <div className="bg-gray-200 rounded-full h-4 shadow-inner overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-[#794696] via-[#ca91cf] to-[#794696] h-4 rounded-full transition-all duration-700 ease-out relative overflow-hidden"
                    style={{ width: `${getProgress()}%` }}
                  >
                    {/* Glitzer-Effekt */}
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
                  </div>
                </div>
              </div>
              
              {/* Schritt-Indikatoren */}
              <div className="flex justify-between items-center px-2">
                {['Foto', 'Daten', 'Transform', 'Kontakt'].map((step, index) => {
                  const stepProgress = (index + 1) * 25;
                  const isComplete = getProgress() >= stepProgress;
                  const isCurrent = getProgress() >= (index * 25) && getProgress() < stepProgress;
                  
                  return (
                    <div key={index} className="flex flex-col items-center gap-1">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-300 ${
                        isComplete 
                          ? 'bg-green-500 text-white scale-110 shadow-lg' 
                          : isCurrent
                          ? 'bg-[#ca91cf] text-white scale-110 shadow-lg animate-pulse'
                          : 'bg-gray-300 text-gray-500'
                      }`}>
                        {isComplete ? '✓' : index + 1}
                      </div>
                      <span className={`text-xs font-medium ${
                        isComplete || isCurrent ? 'text-[#794696]' : 'text-gray-400'
                      }`}>
                        {step}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-8 md:py-12">
        <div className={showResultsOnly ? 'grid grid-cols-1 gap-6 md:gap-8' : transformationResult ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8' : 'grid grid-cols-1 gap-6 md:gap-8'}>
          {/* Input Panel - nur anzeigen wenn nicht Results-Only */}
          {!showResultsOnly && (
            <Card className="p-4 md:p-6 bg-white shadow-md hover:shadow-lg transition-shadow" style={{"borderColor": "#794696"}} data-testid="input-panel">
            <div className="space-y-8">{/* Removed h2 header */}
              
              {/* Foto & Körperdaten Gruppe */}
              <div className="space-y-6">
                {/* Bild Upload/Kamera */}
                <Card className="p-6 bg-gray-50 shadow-lg image-upload-section" style={{"borderColor": "#794696"}}>
                  <div className="space-y-6">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">📷 Ganzkörperfoto</h3>
                        <button
                          onClick={() => setShowPhotoGuide(true)}
                          className="w-6 h-6 bg-[#ca91cf]/20 hover:bg-[#ca91cf]/40 rounded-full flex items-center justify-center text-[#ca91cf] text-sm font-bold transition-all hover:scale-110"
                          title="Foto-Anleitung anzeigen"
                        >
                          ?
                        </button>
                      </div>
                      <p className="text-sm text-gray-600">Wählen Sie Ihre bevorzugte Methode</p>
                    </div>
                  
                  {/* Capture Mode Toggle - Mobile-Optimized Large Buttons */}
                  <div className="flex justify-center">
                    <div className="grid grid-cols-2 bg-gray-100 rounded-2xl p-2 shadow-inner w-full max-w-md">
                      <button
                        onClick={() => {
                          setCaptureMode('camera');
                          // Kamera automatisch starten
                          if (!showCamera && !imagePreview) {
                            startCamera();
                          }
                        }}
                        className={`py-4 px-6 rounded-xl text-base font-bold transition-all flex items-center justify-center gap-2 ${
                          captureMode === 'camera' 
                            ? 'bg-gradient-to-r from-[#ca91cf] to-[#b580ba] text-white shadow-lg transform scale-105' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-xl">📸</span>
                        <span>Bild aufnehmen</span>
                      </button>
                      <button
                        onClick={async () => {
                          console.log('📁 Wechsel zu Upload-Modus');
                          if (showCamera) {
                            await stopCamera();
                          }
                          setCaptureMode('upload');
                        }}
                        className={`py-4 px-6 rounded-xl text-base font-bold transition-all flex items-center justify-center gap-2 ${
                          captureMode === 'upload' 
                            ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-lg transform scale-105' 
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        <span className="text-xl">📁</span>
                        <span>Bild hochladen</span>
                      </button>
                    </div>
                  </div>

                  {/* Upload Mode - Improved design */}
                  {captureMode === 'upload' && (
                    <div 
                      className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#ca91cf] transition-all duration-200 cursor-pointer bg-gradient-to-br from-gray-50 to-white backdrop-blur-sm hover:bg-gray-100"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="image-upload-area"
                    >
                      {imagePreview ? (
                        <div className="space-y-6">
                          {/* Body Detection Canvas with Green Outline - LARGER DISPLAY */}
                          <div className="relative mx-auto" style={{ maxWidth: '600px' }}>
                            <canvas 
                              ref={bodyDetectionCanvasRef}
                              className="w-full h-auto mx-auto rounded-xl shadow-2xl border-2 border-green-400"
                              style={{ maxHeight: '500px', objectFit: 'contain' }}
                            />
                            {detectingBody && (
                              <div className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center">
                                <div className="bg-white px-4 py-3 rounded-lg shadow-lg">
                                  <div className="flex items-center gap-3">
                                    <div className="animate-spin h-5 w-5 border-3 border-[#ca91cf] border-t-transparent rounded-full"></div>
                                    <span className="text-base font-semibold text-gray-700">🔍 Körpererkennung läuft...</span>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="space-y-4">
                            {/* Body Detection Status Message */}
                            {bodyDetectionMessage && (
                              <div className={`p-4 rounded-xl border-2 text-center ${
                                bodyDetected && bodyWithinBoundaries 
                                  ? 'bg-green-50 border-green-400 text-green-800' 
                                  : bodyDetected 
                                  ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                                  : 'bg-red-50 border-red-400 text-red-800'
                              }`}>
                                <p className="text-base font-bold">{bodyDetectionMessage}</p>
                              </div>
                            )}
                            <p className="text-green-600 font-semibold text-xl text-center">✓ Körpererkennung abgeschlossen</p>
                            <Button 
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedImage(null);
                                setImagePreview(null);
                                setBodyDetected(false);
                                setBodyWithinBoundaries(false);
                                setBodyDetectionMessage('');
                              }}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 px-6 py-3 rounded-xl transition-all"
                            >
                              Anderes Bild wählen
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center shadow-lg">
                            <Upload className="h-8 w-8 text-gray-600" />
                          </div>
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">Datei hochladen</h4>
                            <p className="text-gray-600 text-base mb-2 leading-relaxed">
                              Klicken Sie hier oder ziehen Sie ein Ganzkörperbild hierher
                            </p>
                            <p className="text-gray-500 text-sm">
                              Empfohlen: JPG, PNG • Maximale Größe: 10MB
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Camera Mode - Direkt Video anzeigen */}
                  {captureMode === 'camera' && (
                    <div className="space-y-6">
                      {showCamera && (
                        <div className="fixed inset-0 z-50 bg-black">
                          {/* Simple container - no flex */}
                          <div className="relative w-full h-full">
                            {/* Video - Portrait ohne Rotation, KEIN ZOOM */}
                            <video 
                              ref={videoRef}
                              autoPlay 
                              playsInline 
                              muted
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%) scaleX(-1)',  // Nur Spiegelung
                                maxWidth: '100%',
                                maxHeight: '100%',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                display: 'block'  // Verhindert inline-spacing
                              }}
                              onLoadedMetadata={() => {
                                if (videoRef.current) {
                                  videoRef.current.play().catch(e => console.log('Video play failed:', e));
                                }
                              }}
                            />
                            
                            {/* Canvas Overlay für Keypoints */}
                            <canvas
                              ref={overlayCanvasRef}
                              style={{ 
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                maxHeight: '100vh',  // Explizite Begrenzung
                                pointerEvents: 'none',
                                zIndex: 15
                              }}
                            />
                            
                            
                            {/* Auto-Capture Countdown - Zentriert */}
                            {captureCountdown && (
                              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
                                <div className="w-32 h-32 rounded-full bg-green-500/90 flex items-center justify-center shadow-2xl animate-pulse">
                                  <span className="text-7xl font-bold text-white">{captureCountdown}</span>
                                </div>
                              </div>
                            )}
                            
                            {/* Positionierungs-Hilfestellung - Oben Zentral */}
                            {!captureCountdown && (
                              <div className="absolute top-0 left-0 right-0 z-20 pointer-events-none" style={{ top: 'calc(env(safe-area-inset-top, 0px) + 60px)' }}>
                                <div className="flex flex-col items-center gap-3 px-4">
                                  {/* Status Indicator */}
                                  <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl shadow-2xl transition-all ${
                                    poseGuidance.bodyDetected && poseGuidance.centered && poseGuidance.distanceOk && poseGuidance.fullBodyVisible
                                      ? 'bg-green-500 text-white'
                                      : poseGuidance.bodyDetected
                                      ? 'bg-yellow-500 text-white'
                                      : 'bg-red-500 text-white'
                                  }`}>
                                    {poseGuidance.bodyDetected && poseGuidance.centered && poseGuidance.distanceOk && poseGuidance.fullBodyVisible 
                                      ? '✅' 
                                      : poseGuidance.bodyDetected 
                                      ? '⚠️' 
                                      : '❌'}
                                  </div>
                                  
                                  {/* Haupt-Nachricht - In App-Farben (Lila/Purple) mit Transparenz */}
                                  <div className="backdrop-blur-md px-6 py-3 rounded-2xl shadow-2xl max-w-sm" style={{ backgroundColor: 'rgba(202, 145, 207, 0.85)' }}>
                                    <p className="text-white text-lg font-bold text-center">
                                      {poseGuidance.message}
                                    </p>
                                  </div>
                                  
                                  {/* Detaillierte Anweisung - In App-Farben (Lila/Purple) mit Transparenz */}
                                  {poseGuidance.instruction && (
                                    <div className="backdrop-blur-sm px-5 py-2 rounded-xl shadow-xl max-w-sm" style={{ backgroundColor: 'rgba(202, 145, 207, 0.75)' }}>
                                      <p className="text-white text-sm font-semibold text-center">
                                        {poseGuidance.instruction}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Button Layout - RESPONSIVE für Hoch- und Querformat */}
                            
                            {/* HOCHFORMAT Layout - Nur im Portrait sichtbar */}
                            {/* Hauptbutton - Foto - Unten Mitte */}
                            <button
                              onClick={capturePhoto}
                              className="portrait-buttons absolute left-1/2 transform -translate-x-1/2 z-20 w-16 h-16 bg-white/90 hover:bg-white active:bg-gray-200 text-gray-900 rounded-full font-bold shadow-xl flex items-center justify-center text-3xl border-3 border-green-500 active:scale-90 transition-all touch-manipulation"
                              style={{ 
                                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)'
                              }}
                              aria-label="Foto aufnehmen"
                            >
                              📸
                            </button>
                            
                            {/* Schließen Button - Unten Links */}
                            <button
                              onClick={async () => {
                                console.log('🔴 Kamera-Schließen-Button geklickt');
                                await stopCamera();
                              }}
                              className="portrait-buttons absolute z-20 px-4 py-3 bg-red-600/85 hover:bg-red-700 active:bg-red-800 text-white rounded-full font-bold shadow-xl flex items-center justify-center text-sm backdrop-blur-sm active:scale-90 transition-all touch-manipulation"
                              style={{ 
                                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
                                left: '12px'
                              }}
                              aria-label="Kamera schließen"
                            >
                              Abbrechen
                            </button>
                            
                            {/* Auto/Manuell Toggle - Unten Rechts */}
                            <button
                              onClick={() => setAutoCapture(!autoCapture)}
                              className={`portrait-buttons absolute z-20 w-14 h-14 ${autoCapture ? 'bg-green-600/85' : 'bg-gray-600/85'} hover:opacity-90 active:opacity-100 text-white rounded-full font-bold shadow-xl flex items-center justify-center backdrop-blur-sm active:scale-90 transition-all touch-manipulation text-2xl`}
                              style={{ 
                                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 80px)',
                                right: '12px'
                              }}
                              aria-label={autoCapture ? "Auto-Modus aktiv" : "Manueller Modus"}
                            >
                              {autoCapture ? '🤖' : '👆'}
                            </button>
                            
                            {/* Kamera wechseln - Oben Rechts */}
                            <button
                              onClick={switchCamera}
                              className="portrait-buttons absolute z-20 w-12 h-12 bg-black/70 hover:bg-black/80 active:bg-black/60 rounded-full text-white font-semibold transition-all shadow-xl backdrop-blur-sm touch-manipulation active:scale-95 flex items-center justify-center"
                              style={{ 
                                top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
                                right: '12px'
                              }}
                              title="Kamera wechseln"
                            >
                              <span className="text-xl">🔄</span>
                            </button>
                            
                            {/* QUERFORMAT Layout - Nur im Landscape sichtbar */}
                            {/* Hauptbutton - Foto - Rechts Mitte */}
                            <button
                              onClick={capturePhoto}
                              className="landscape-buttons absolute top-1/2 transform -translate-y-1/2 z-20 w-20 h-20 bg-white/90 hover:bg-white active:bg-gray-200 text-gray-900 rounded-full font-bold shadow-xl flex items-center justify-center text-4xl border-4 border-green-500 active:scale-90 transition-all touch-manipulation"
                              style={{ 
                                right: 'calc(env(safe-area-inset-right, 0px) + 20px)'
                              }}
                              aria-label="Foto aufnehmen"
                            >
                              📸
                            </button>
                            
                            {/* Schließen Button - Links Oben */}
                            <button
                              onClick={stopCamera}
                              className="landscape-buttons absolute z-20 px-4 py-3 bg-red-600/85 hover:bg-red-700 active:bg-red-800 text-white rounded-full font-bold shadow-xl flex items-center justify-center text-sm backdrop-blur-sm active:scale-90 transition-all touch-manipulation"
                              style={{ 
                                top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
                                left: 'calc(env(safe-area-inset-left, 0px) + 16px)'
                              }}
                              aria-label="Kamera schließen"
                            >
                              Abbrechen
                            </button>
                            
                            {/* Auto/Manuell Toggle - Rechts Oben */}
                            <button
                              onClick={() => setAutoCapture(!autoCapture)}
                              className={`landscape-buttons absolute z-20 w-14 h-14 ${autoCapture ? 'bg-green-600/85' : 'bg-gray-600/85'} hover:opacity-90 active:opacity-100 text-white rounded-full font-bold shadow-xl flex items-center justify-center backdrop-blur-sm active:scale-90 transition-all touch-manipulation text-2xl`}
                              style={{ 
                                top: 'calc(env(safe-area-inset-top, 0px) + 16px)',
                                right: 'calc(env(safe-area-inset-right, 0px) + 16px)'
                              }}
                              aria-label={autoCapture ? "Auto-Modus aktiv" : "Manueller Modus"}
                            >
                              {autoCapture ? '🤖' : '👆'}
                            </button>
                            
                            {/* Kamera wechseln Button - Links Unten */}
                            <button
                              onClick={switchCamera}
                              className="landscape-buttons absolute z-20 w-14 h-14 bg-black/70 hover:bg-black/80 active:bg-black/60 rounded-full text-white font-semibold transition-all shadow-xl backdrop-blur-sm touch-manipulation active:scale-95 flex items-center justify-center"
                              style={{ 
                                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
                                left: 'calc(env(safe-area-inset-left, 0px) + 16px)'
                              }}
                              title="Kamera wechseln"
                            >
                              <span className="text-2xl">🔄</span>
                            </button>
                          </div>
                        </div>
                      )}

                      {imagePreview && captureMode === 'camera' && (
                        <div className="border-2 border-[#ca91cf]/50 rounded-lg p-4 bg-[#ca91cf]/5">
                          <div className="space-y-4 text-center">
                            {/* Body Detection Canvas with Green Outline - LARGER for camera */}
                            <div className="relative mx-auto" style={{ maxWidth: '500px' }}>
                              <canvas 
                                ref={bodyDetectionCanvasRef}
                                className="w-full h-auto mx-auto rounded-lg shadow-2xl border-2 border-green-400"
                                style={{ maxHeight: '450px', objectFit: 'contain' }}
                              />
                              {detectingBody && (
                                <div className="absolute inset-0 bg-black/40 rounded-lg flex items-center justify-center">
                                  <div className="bg-white px-3 py-2 rounded-lg shadow-lg">
                                    <div className="flex items-center gap-2">
                                      <div className="animate-spin h-5 w-5 border-2 border-[#ca91cf] border-t-transparent rounded-full"></div>
                                      <span className="text-sm font-semibold text-gray-700">🔍 Körpererkennung...</span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="space-y-2">
                              {/* Body Detection Status Message */}
                              {bodyDetectionMessage && (
                                <div className={`p-3 rounded-lg border-2 text-base ${
                                  bodyDetected && bodyWithinBoundaries 
                                    ? 'bg-green-50 border-green-400 text-green-800' 
                                    : bodyDetected 
                                    ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
                                    : 'bg-red-50 border-red-400 text-red-800'
                                }`}>
                                  <p className="font-bold">{bodyDetectionMessage}</p>
                                </div>
                              )}
                              <p className="text-[#ca91cf] font-semibold text-lg">📸 Körpererkennung abgeschlossen ✓</p>
                              <Button 
                                onClick={() => {
                                  setSelectedImage(null);
                                  setImagePreview(null);
                                  setBodyDetected(false);
                                  setBodyWithinBoundaries(false);
                                  setBodyDetectionMessage('');
                                }}
                                className="text-sm bg-purple-600/20 hover:bg-purple-600/40 text-purple-300 border border-purple-500/50"
                              >
                                Neues Foto aufnehmen
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Hidden Elements */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <canvas ref={captureCanvasRef} style={{ display: 'none' }} />
                  <video ref={videoRef} style={{ display: 'none' }} />
                </div>
              </Card>

                {/* Körperdaten */}
                <Card className="p-6 bg-gray-50 shadow-lg body-data-section" style={{"borderColor": "#794696"}}>
                  <div className="space-y-6">
                    <div className="text-center">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">👤 Körperdaten</h3>
                      <p className="text-sm text-gray-600">Ihre aktuellen Maße für die Berechnung</p>
                    </div>
                    
                    {/* Weight and Height - Enhanced numeric inputs */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label className="font-medium text-gray-700 text-base flex items-center gap-2">
                          ⚖️ Aktuelles Gewicht (kg) *
                        </Label>
                        <div className="relative">
                          <Input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min="50"
                            max="200"
                            step="0.5"
                            value={currentWeight}
                            onChange={(e) => setCurrentWeight(e.target.value)}
                            className={`bg-white text-gray-900 py-3 px-4 pr-12 rounded-xl shadow-sm transition-all text-lg font-medium ${
                              currentWeight && validateWeight(currentWeight) 
                                ? 'border-red-500 focus:border-red-500' 
                                : currentWeight && !validateWeight(currentWeight)
                                ? 'border-[#ca91cf] focus:border-[#ca91cf]'
                                : 'border-gray-300 hover:border-gray-400 focus:border-[#ca91cf]'
                            }`}
                            placeholder="80"
                            data-testid="weight-input"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                            {currentWeight && !validateWeight(currentWeight) && (
                              <span className="text-[#ca91cf] text-sm">✓</span>
                            )}
                            {currentWeight && validateWeight(currentWeight) && (
                              <span className="text-red-500 text-sm">⚠️</span>
                            )}
                            <span className="text-gray-500 text-sm font-medium">kg</span>
                          </div>
                          <div className="flex justify-between text-xs mt-1">
                            <span className="text-gray-500">Min: 50kg</span>
                            {currentWeight && validateWeight(currentWeight) && (
                              <span className="text-red-400">{validateWeight(currentWeight)}</span>
                            )}
                            <span className="text-gray-500">Max: 200kg</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <Label className="font-medium text-gray-700 text-base flex items-center gap-2">
                          📏 Körpergröße (cm) *
                        </Label>
                        <div className="relative">
                          <Input
                            type="number"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            min="150"
                            max="220"
                            step="1"
                            value={height}
                            onChange={(e) => setHeight(e.target.value)}
                            className={`bg-white text-gray-900 py-3 px-4 pr-12 rounded-xl shadow-sm transition-all text-lg font-medium ${
                              height && validateHeight(height) 
                                ? 'border-red-500 focus:border-red-500' 
                                : height && !validateHeight(height)
                                ? 'border-[#ca91cf] focus:border-[#ca91cf]'
                                : 'border-gray-300 hover:border-gray-400 focus:border-[#ca91cf]'
                            }`}
                            placeholder="175"
                            data-testid="height-input"
                          />
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-1">
                            {height && !validateHeight(height) && (
                              <span className="text-[#ca91cf] text-sm">✓</span>
                            )}
                            {height && validateHeight(height) && (
                              <span className="text-red-500 text-sm">⚠️</span>
                            )}
                            <span className="text-purple-400 text-sm font-medium">cm</span>
                          </div>
                          <div className="flex justify-between text-xs mt-1">
                            <span className="text-purple-400">Min: 150cm</span>
                            {height && validateHeight(height) && (
                              <span className="text-red-400">{validateHeight(height)}</span>
                            )}
                            <span className="text-purple-400">Max: 220cm</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Behandlungsparameter Gruppe */}
              <Card className="p-5 md:p-6 bg-gray-50 border-gray-200 shadow-md">
                <div className="space-y-6 md:space-y-8">

              {/* Treatments - Enhanced with larger buttons */}
              <div className="space-y-4">
                <Label className="font-medium text-gray-700 text-lg">Anzahl Behandlungen *</Label>
                
                {/* Large Button Grid for Treatments */}
                <div className="grid grid-cols-2 gap-3 md:gap-4">
                  {[
                    { value: 10, label: "10 Behandlungen", subtitle: "5 Wochen", emoji: "🌱" },
                    { value: 20, label: "20 Behandlungen", subtitle: "10 Wochen", emoji: "🌿" },
                    { value: 30, label: "30 Behandlungen", subtitle: "15 Wochen", emoji: "🌳" },
                    { value: 40, label: "40 Behandlungen", subtitle: "20 Wochen", emoji: "🏆" }
                  ].map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTreatments(option.value)}
                      className={`p-4 md:p-6 rounded-xl border-2 transition-all duration-200 hover:scale-105 ${
                        treatments === option.value
                          ? 'border-[#ca91cf] bg-gradient-to-br from-[#ca91cf]/20 to-[#794696]/10 shadow-lg'
                          : 'border-gray-300 bg-white hover:border-[#ca91cf]/50 hover:bg-gray-50'
                      }`}
                      data-testid={`treatment-${option.value}`}
                    >
                      <div className="text-center space-y-1">
                        <div className="text-xl md:text-2xl">{option.emoji}</div>
                        <div className={`font-bold text-sm md:text-base ${
                          treatments === option.value ? 'text-[#794696]' : 'text-gray-700'
                        }`}>
                          {option.label}
                        </div>
                        <div className={`text-xs md:text-sm ${
                          treatments === option.value ? 'text-[#ca91cf]' : 'text-gray-500'
                        }`}>
                          {option.subtitle}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Diet - With Tooltip */}
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <Label className="font-medium text-gray-700 text-base">
                    Diät: {dietCalories[0]} {dietCalories[0] === 2500 ? '(keine Diät)' : 'Kalorien/Tag'}
                  </Label>
                  <div className="group relative">
                    <div className="w-5 h-5 bg-blue-500/20 rounded-full flex items-center justify-center cursor-help border border-blue-500/40 hover:bg-blue-500/30 transition-all">
                      <span className="text-xs text-blue-400">💡</span>
                    </div>
                    <div className="absolute left-6 top-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bg-white text-gray-900 text-sm rounded-xl p-4 shadow-xl border border-gray-300 z-50 w-80 backdrop-blur-sm">
                      <p className="leading-relaxed">
                        <strong className="text-blue-600">Eine Diät ist für eine erfolgreiche Körpertransformation bei uns nicht notwendig.</strong> Aber eine kalorienarme eiweißreiche Ernährung unterstützt den Prozess natürlich.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-2 py-4">
                  <div className="relative">
                    <input
                      type="range"
                      min={1000}
                      max={2500}
                      step={50}
                      value={3500 - dietCalories[0]} // Umkehrung: 3500 - aktueller Wert
                      onChange={(e) => setDietCalories([3500 - parseInt(e.target.value)])} // Umkehrung der Eingabe
                      className="custom-slider w-full"
                      data-testid="diet-slider"
                    />
                    <div 
                      className="absolute top-2 h-2 bg-gradient-to-r from-[#794696] to-[#ca91cf] rounded-full pointer-events-none"
                      style={{
                        width: `${((3500 - dietCalories[0] - 1000) / (2500 - 1000)) * 100}%`,
                        left: '0'
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-sm text-gray-500 font-medium">
                  <span>2500 kcal (keine Diät)</span>
                  <span>1000 kcal</span>
                </div>
              </div>

              {/* Sport - With Tooltip */}
              <div className="space-y-5">
                <div className="flex items-center gap-3">
                  <Label className="font-medium text-black text-base">
                    Sport: {sportHours[0]} Stunden/Woche
                  </Label>
                  <div className="group relative">
                    <div className="w-5 h-5 bg-[#ca91cf]/20 rounded-full flex items-center justify-center cursor-help border border-[#ca91cf]/40 hover:bg-[#ca91cf]/30 transition-all">
                      <span className="text-xs text-[#ca91cf]">💪</span>
                    </div>
                    <div className="absolute left-6 top-0 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 bg-white text-gray-900 text-sm rounded-xl p-4 shadow-xl border border-gray-300 z-50 w-72 backdrop-blur-sm">
                      <p className="leading-relaxed">
                        <strong className="text-[#ca91cf]">Auch Sport ist bei uns nicht nötig</strong> aber unterstützt trotzdem die Transformation.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="px-2 py-4">
                  <div className="relative">
                    <input
                      type="range"
                      min={0}
                      max={14}
                      step={0.5}
                      value={sportHours[0]}
                      onChange={(e) => setSportHours([parseFloat(e.target.value)])}
                      className="custom-slider w-full"
                      data-testid="sport-slider"
                    />
                    <div 
                      className="absolute top-2 h-2 bg-gradient-to-r from-[#794696] to-[#ca91cf] rounded-full pointer-events-none"
                      style={{
                        width: `${(sportHours[0] / 14) * 100}%`,
                        left: '0'
                      }}
                    />
                  </div>
                </div>
                <div className="flex justify-between text-sm text-gray-500 font-medium">
                  <span>0h</span>
                  <span>14h</span>
                </div>
              </div>

                </div>
              </Card>

              {/* Berechnung */}
              {/* Action Buttons - Improved styling */}
                <Card className="p-5 md:p-6 bg-gradient-to-r from-gray-900/60 to-gray-800/60 border-[#ca91cf]/30 shadow-lg">
                  <div className="space-y-5 md:space-y-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="flex-1 bg-gradient-to-r from-[#ca91cf] to-[#b580ba] hover:from-[#b580ba] hover:to-[#a070a5] text-white py-4 px-8 rounded-xl text-lg font-semibold shadow-lg transform hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:transform-none transformation-button"
                        data-testid="calculate-button"
                      >
                        {loading ? (
                          <span className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Berechnung läuft...
                          </span>
                        ) : (
                          '🚀 Transformation starten'
                        )}
                      </Button>
                      <Button
                        onClick={resetForm}
                        variant="outline"
                        className="border-[#ca91cf]/40 text-[#ca91cf] hover:bg-[#ca91cf]/20 hover:border-[#ca91cf]/60 py-4 px-6 rounded-xl font-medium transition-all"
                        data-testid="reset-button"
                      >
                        🔄 Zurücksetzen
                      </Button>
                    </div>
                    {transformationResult && !transformedImage && (
                      <Button
                        onClick={() => processImageWithMoveNet(transformationResult)}
                        className="w-full bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white py-3 px-6 rounded-xl font-medium shadow-lg transition-all"
                        data-testid="retry-transformation-button"
                      >
                        🔄 Bild neu berechnen
                      </Button>
                    )}
                  </div>
                </Card>
              </div>
            </Card>
          )}

          {/* Motivation Section - Show when no transformation yet */}
          {!transformationResult && (selectedImage || currentWeight || height) && (
            <div className="space-y-5 md:space-y-6">
              {/* Success Stories */}
              <Card className="p-5 md:p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-md">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-green-800 mb-2 flex items-center justify-center gap-2">
                    ⭐ Erfolgsgeschichten
                  </h3>
                  <p className="text-green-600 text-sm">
                    Echte Ergebnisse unserer Kunden in Basel-Landschaft
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-green-100">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">-12 kg</div>
                      <div className="text-sm text-gray-600 mt-1">in 8 Wochen</div>
                      <div className="text-xs text-gray-500 mt-2">"Unglaublich!"</div>
                      <div className="text-xs text-gray-400">- Sarah M., Liestal</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-green-100">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">-18 kg</div>
                      <div className="text-sm text-gray-600 mt-1">in 12 Wochen</div>
                      <div className="text-xs text-gray-500 mt-2">"Endlich geschafft!"</div>
                      <div className="text-xs text-gray-400">- Michael K., Pratteln</div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 shadow-sm border border-green-100">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-600">-25 kg</div>
                      <div className="text-sm text-gray-600 mt-1">in 16 Wochen</div>
                      <div className="text-xs text-gray-500 mt-2">"Leben verändert!"</div>
                      <div className="text-xs text-gray-400">- Anna L., Muttenz</div>
                    </div>
                  </div>
                </div>
              </Card>

              {/* Quick Benefits */}
              <Card className="p-5 md:p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-md">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold text-blue-800 mb-2">
                    ✨ In 30 Sekunden zu Ihrem Traumkörper
                  </h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div className="space-y-2">
                    <div className="text-2xl">🚀</div>
                    <div className="text-sm font-medium text-blue-700">Sofortige Ergebnisse</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl">📱</div>
                    <div className="text-sm font-medium text-blue-700">Ohne App Download</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl">🔒</div>
                    <div className="text-sm font-medium text-blue-700">100% Privat</div>
                  </div>
                  <div className="space-y-2">
                    <div className="text-2xl">💰</div>
                    <div className="text-sm font-medium text-blue-700">Kostenlos</div>
                  </div>
                </div>
              </Card>
              
              {/* Urgency Reminder with 60% Discount */}
              {showUrgencyBanner && countdownTime > 0 && (
                <Card className="p-6 bg-gradient-to-r from-[#794696]/20 to-[#ca91cf]/20 border-2 border-[#ca91cf] shadow-lg animate-pulse">
                  <div className="text-center space-y-4">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-2xl animate-bounce">⚡</span>
                      <h3 className="text-xl font-bold text-[#794696]">
                        NUR NOCH {formatCountdown(countdownTime)}
                      </h3>
                      <span className="text-2xl animate-bounce">⚡</span>
                    </div>
                    
                    <div className="bg-gradient-to-r from-[#794696] to-[#ca91cf] text-white p-4 rounded-xl shadow-lg">
                      <div className="text-xl md:text-2xl font-bold mb-2">-60% + 10% EXTRA</div>
                      <div className="text-base md:text-lg">auf Ihre erste Probebehandlung!</div>
                      <div className="text-sm mt-2 opacity-90">
                        Sichere dir zusätzlich zu unserer -60% Aktion noch einmal 10% Ersparnis
                      </div>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}

          {/* Results Panel - Only show when needed */}
          {transformationResult && (
            <Card className="p-5 md:p-8 bg-white border-gray-200 shadow-lg animate-in slide-in-from-right-5 duration-500" data-testid="results-panel">
              <div className="text-center mb-6 md:mb-8">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-3">
                  <span className="bg-gradient-to-r from-[#ca91cf] to-[#794696] bg-clip-text text-transparent">
                    🎉 Ihre Transformations-Ergebnisse
                  </span>
                </h2>
                <p className="text-gray-600 text-sm md:text-base">Ihre persönliche Transformation</p>
              </div>

              <div className="space-y-6">
                {/* Statistics - Improved design with consistent spacing */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  <div className="bg-gradient-to-br from-[#794696]/10 to-purple-100/50 rounded-xl p-5 md:p-6 border border-[#794696]/20 shadow-md hover:shadow-lg transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#794696]/20 rounded-full flex items-center justify-center">
                        <span className="text-xl">🎯</span>
                      </div>
                      <p className="text-gray-700 font-semibold text-base md:text-lg">Zielgewicht</p>
                    </div>
                    <p className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="target-weight">
                      {transformationResult.target_weight} <span className="text-xl md:text-2xl text-gray-600">kg</span>
                    </p>
                  </div>
                  <div className="bg-gradient-to-br from-[#ca91cf]/10 to-pink-100/50 rounded-xl p-5 md:p-6 border border-[#ca91cf]/20 shadow-md hover:shadow-lg transition-all">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-[#ca91cf]/20 rounded-full flex items-center justify-center">
                        <span className="text-xl">⬇️</span>
                      </div>
                      <p className="text-gray-700 font-semibold text-base md:text-lg">Gewichtsverlust</p>
                    </div>
                    <p className="text-3xl md:text-4xl font-bold text-gray-900" data-testid="weight-loss-percentage">
                      -{(currentWeight - transformationResult.target_weight).toFixed(1)} <span className="text-xl md:text-2xl text-gray-600">kg</span>
                    </p>
                  </div>
                </div>

                {/* Image Comparison - White background with optimized colors */}
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">🔄 Bild-Vergleich</h3>
                    <p className="text-sm md:text-base text-gray-700">Vorher vs. Nachher Transformation</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                    {/* Original Image */}
                    <div className="space-y-4">
                      <div className="text-center">
                        <Label className="text-gray-900 text-lg md:text-xl font-semibold">📷 Vorher</Label>
                      </div>
                      <div className="relative rounded-xl overflow-hidden border-2 border-gray-300 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
                        <img 
                          src={transformationResult.original_image} 
                          alt="Original" 
                          className="w-full h-auto max-h-[400px] md:max-h-[500px] object-contain hover:scale-[1.02] transition-transform duration-300 cursor-zoom-in touch-manipulation"
                          onClick={() => setZoomedImage(transformationResult.original_image)}
                          data-testid="original-image"
                        />
                      </div>
                    </div>

                    {/* Transformed Image */}
                    <div className="space-y-4">
                      <div className="text-center">
                        <Label className="text-[#ca91cf] text-lg md:text-xl font-semibold">✨ Nachher</Label>
                      </div>
                      <div className="relative rounded-xl overflow-hidden border-2 border-[#ca91cf]/50 bg-white shadow-lg hover:shadow-xl transition-all duration-300">
                        {transformedImage ? (
                          <img 
                            src={transformedImage} 
                            alt="Transformed" 
                            className="w-full h-auto max-h-[400px] md:max-h-[500px] object-contain hover:scale-[1.02] transition-transform duration-300 cursor-zoom-in touch-manipulation"
                            onClick={() => setZoomedImage(transformedImage)}
                            data-testid="transformed-image"
                          />
                        ) : transformationResult ? (
                          <div className="w-full h-[400px] bg-white flex items-center justify-center rounded-xl">
                            <div className="text-center p-8 w-full max-w-md">
                              <div className="w-20 h-20 border-4 border-[#ca91cf] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                              <p className="text-gray-900 text-xl font-semibold mb-6">✨ Transformation läuft...</p>
                              
                              {/* Professional Progress Bar */}
                              <div className="w-full bg-gray-200 rounded-full h-4 mb-6 shadow-inner">
                                <div className="bg-gradient-to-r from-[#ca91cf] to-[#794696] h-4 rounded-full shadow-lg transition-all duration-1000 ease-out animate-pulse" 
                                     style={{width: '75%'}}></div>
                              </div>
                              
                              <div className="space-y-4">
                                <p className="text-gray-700 text-sm animate-pulse">
                                  🔬 KI-Algorithmen berechnen Ihre Transformation...
                                </p>
                                <p className="text-gray-600 text-xs">
                                  Bitte haben Sie einen Moment Geduld
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full h-[400px] bg-white flex items-center justify-center rounded-xl">
                            <p className="text-gray-700 text-lg">Keine Transformation verfügbar</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Lead Generation - Only show when transformation is complete */}
                {transformedImage && (
                  <div className="space-y-4 mt-6">
                    <div className="border-t border-gray-200 pt-6">
                      <div className="bg-gradient-to-r from-[#794696]/5 to-[#ca91cf]/5 rounded-xl p-5 md:p-6 border border-[#794696]/20 shadow-sm">
                        <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3 md:mb-4">
                          🌟 Möchten Sie mehr über "Abnehmen im Liegen Baselland" erfahren?
                        </h3>
                        <p className="text-gray-600 text-sm md:text-base mb-5 md:mb-6">
                          Entdecken Sie, wie Sie Ihre Transformation in der Realität erreichen können.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
                          <Button
                            onClick={() => setShowLeadModal(true)}
                            className="bg-gradient-to-r from-[#ca91cf] to-[#b580ba] hover:from-[#b580ba] hover:to-[#a070a5] text-white px-6 py-3 font-semibold shadow-md hover:shadow-lg transition-all"
                          >
                            ✅ Ja, ich möchte mehr erfahren
                          </Button>
                          <Button
                            onClick={handleNoInterest}
                            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 font-medium shadow-sm"
                          >
                            ❌ Nein, danke
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* WordPress Upload Status - Remove this section */}
                {false && transformationResult.wordpress_upload_url && (
                  <div className="bg-[#ca91cf]/10 rounded-lg p-4 border border-[#ca91cf]/20">
                    <p className="text-[#ca91cf] text-sm mb-2">✓ Erfolgreich zu WordPress hochgeladen</p>
                    <a 
                      href={transformationResult.wordpress_upload_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-[#ca91cf] hover:text-[#b580ba] text-xs underline"
                      data-testid="wordpress-link"
                    >
                      {transformationResult.wordpress_upload_url}
                    </a>
                  </div>
                )}
              </div>

              {/* Neue Transformation Button - nur im Results-Only Modus */}
              {showResultsOnly && (
                <div className="text-center pt-6 border-t border-gray-200">
                  <Button
                    onClick={resetForm}
                    className="bg-gradient-to-r from-[#794696] to-[#ca91cf] hover:from-[#6a3d87] hover:to-[#b580ba] text-white px-8 py-4 rounded-xl text-lg font-semibold shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    🔄 Neue Transformation starten
                  </Button>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>

      {/* Canvas for image processing (hidden) */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Analytics Dashboard */}
      {showAnalytics && (
        <AnalyticsDashboard 
          onClose={() => setShowAnalytics(false)}
        />
      )}

      {/* Photo Guide Modal */}
      {showPhotoGuide && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto" onClick={() => setShowPhotoGuide(false)}>
          <div className="bg-white rounded-2xl p-4 md:p-6 max-w-2xl w-full mx-4 border border-gray-200 max-h-[90vh] overflow-y-auto shadow-2xl my-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                📸 Perfekte Foto-Anleitung
              </h2>
              <p className="text-gray-600">Für optimale Transformationsergebnisse</p>
            </div>

            <div className="space-y-4 md:space-y-6">
              <div className="bg-[#ca91cf]/10 rounded-xl p-4 md:p-6 border border-[#ca91cf]/30">
                <h3 className="text-[#794696] font-semibold text-base md:text-lg mb-3 flex items-center gap-2">
                  📷 Kamera-Setup
                </h3>
                <ul className="space-y-2 text-sm md:text-base text-gray-700">
                  <li>• <strong>Vollkörper:</strong> Ganzer Körper von Kopf bis Fuß sichtbar</li>
                  <li>• <strong>Gerader Stand:</strong> Aufrecht stehen, Arme seitlich</li>
                  <li>• <strong>Frontale Ansicht:</strong> Direkt zur Kamera schauen</li>
                  <li>• <strong>Gute Beleuchtung:</strong> Helles, gleichmäßiges Licht</li>
                </ul>
              </div>

              <div className="bg-green-50 rounded-xl p-4 md:p-6 border border-green-200">
                <h3 className="text-green-700 font-semibold text-base md:text-lg mb-3 flex items-center gap-2">
                  ✅ Optimal
                </h3>
                <ul className="space-y-2 text-sm md:text-base text-green-800">
                  <li>• Neutraler, einfarbiger Hintergrund</li>
                  <li>• Enganliegende Kleidung oder Unterwäsche</li>
                  <li>• 2-3 Meter Abstand zur Kamera</li>
                  <li>• Handy im Hochformat halten</li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-4 md:p-6 border border-red-200">
                <h3 className="text-red-700 font-semibold text-base md:text-lg mb-3 flex items-center gap-2">
                  ❌ Vermeiden
                </h3>
                <ul className="space-y-2 text-sm md:text-base text-red-800">
                  <li>• Weite, lockere Kleidung</li>
                  <li>• Unruhiger oder gemusterter Hintergrund</li>
                  <li>• Schlechte Beleuchtung oder Schatten</li>
                  <li>• Angeschnittene Körperteile</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 md:mt-8 text-center">
              <Button
                onClick={() => setShowPhotoGuide(false)}
                className="bg-gradient-to-r from-[#794696] to-[#ca91cf] hover:from-[#6a3d87] hover:to-[#b580ba] text-white px-6 md:px-8 py-3 rounded-xl font-semibold shadow-lg transition-all text-sm md:text-base"
              >
                ✨ Verstanden, loslegen!
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setZoomedImage(null)}>
          <div className="relative max-w-6xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <img 
              src={zoomedImage} 
              alt="Zoomed view" 
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button 
              onClick={() => setZoomedImage(null)}
              className="absolute top-4 right-4 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white text-xl font-bold transition-all hover:scale-110"
            >
              ✕
            </button>
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 px-4 py-2 rounded-lg">
              <p className="text-white text-sm">🔍 Klicken Sie außerhalb des Bildes zum Schließen</p>
            </div>
          </div>
        </div>
      )}

      {/* Lead Generation Modal - Verbessertes Design */}
      {showLeadModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={() => setShowLeadModal(false)}>
          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-7 md:p-8 w-[90vw] max-w-[340px] sm:max-w-md mx-auto shadow-2xl max-h-[95vh] overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
            
            {/* Header mit Gradient */}
            <div className="text-center mb-4 w-full">
              <div className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#ca91cf] to-[#b580ba] rounded-full mb-3">
                <span className="text-xl sm:text-2xl">🎯</span>
              </div>
              <h2 className="main-title text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-2 break-words leading-tight">
                Ihr Weg zur Traumfigur
              </h2>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed break-words">
                Sichern Sie sich jetzt <span className="font-bold text-[#ca91cf]">-60% + 10% EXTRA</span> auf Ihre erste Behandlung
              </p>
            </div>

            {/* Trust Badges - Vertikal gestapelt */}
            <div className="flex flex-col gap-2 mb-4 p-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl w-full">
              <div className="flex items-center gap-2">
                <div className="text-lg flex-shrink-0">⭐</div>
                <div className="text-xs text-gray-700 font-medium">98% Zufrieden</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-lg flex-shrink-0">🔒</div>
                <div className="text-xs text-gray-700 font-medium">100% Sicher</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="text-lg flex-shrink-0">⚡</div>
                <div className="text-xs text-gray-700 font-medium">Schnell & Easy</div>
              </div>
            </div>

            {/* Form mit Icons */}
            <div className="space-y-3 mb-4 w-full">
              {/* Name Input */}
              <div className="w-full">
                <Label className="text-gray-700 font-semibold text-xs sm:text-sm mb-1.5 block">
                  Ihr Name *
                </Label>
                <div className="relative w-full">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                    👤
                  </div>
                  <Input
                    type="text"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                    className="pl-10 pr-3 py-3 text-sm bg-gray-50 border-2 border-gray-200 focus:border-[#ca91cf] focus:ring-4 focus:ring-[#ca91cf]/20 rounded-xl transition-all w-full"
                    placeholder="z.B. Anna Müller"
                  />
                </div>
              </div>

              {/* Mobile Input */}
              <div className="w-full">
                <Label className="text-gray-700 font-semibold text-xs sm:text-sm mb-1.5 block">
                  Mobilnummer *
                </Label>
                <div className="relative w-full">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                    📱
                  </div>
                  <Input
                    type="tel"
                    value={leadForm.mobile}
                    onChange={(e) => setLeadForm({...leadForm, mobile: e.target.value})}
                    className="pl-10 pr-3 py-3 text-sm bg-gray-50 border-2 border-gray-200 focus:border-[#ca91cf] focus:ring-4 focus:ring-[#ca91cf]/20 rounded-xl transition-all w-full"
                    placeholder="+41 79 123 45 67"
                  />
                </div>
              </div>

              {/* Email Input */}
              <div className="w-full">
                <Label className="text-gray-700 font-semibold text-xs sm:text-sm mb-1.5 block">
                  E-Mail-Adresse *
                </Label>
                <div className="relative w-full">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-base">
                    ✉️
                  </div>
                  <Input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                    className="pl-10 pr-3 py-3 text-sm bg-gray-50 border-2 border-gray-200 focus:border-[#ca91cf] focus:ring-4 focus:ring-[#ca91cf]/20 rounded-xl transition-all w-full"
                    placeholder="anna@beispiel.com"
                  />
                </div>
              </div>
            </div>

            {/* Benefits Liste */}
            <div className="mb-4 p-3 bg-gray-50 rounded-xl w-full">
              <p className="text-xs font-semibold text-gray-700 mb-2 break-words">✨ Das erwartet Sie:</p>
              <ul className="space-y-1.5 text-xs text-gray-600">
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                  <span className="break-words">Kostenlose Erstberatung</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                  <span className="break-words">Individuelle Behandlungsplanung</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 mr-2 mt-0.5 flex-shrink-0">✓</span>
                  <span className="break-words">Keine Verpflichtung, kostenlose Stornierung</span>
                </li>
              </ul>
            </div>

            {/* Buttons */}
            <div className="space-y-2.5 w-full">
              <Button
                onClick={handleLeadSubmit}
                disabled={leadSubmitting}
                className="w-full bg-gradient-to-r from-[#ca91cf] to-[#b580ba] hover:from-[#b580ba] hover:to-[#a070a5] text-white py-3.5 px-4 text-sm sm:text-base font-bold rounded-xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
              >
                {leadSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-sm">Wird gesendet...</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <span className="text-lg sm:text-xl">🎁</span>
                    <span className="text-sm">Jetzt -60% + 10% EXTRA sichern</span>
                  </span>
                )}
              </Button>
              
              <Button
                onClick={() => setShowLeadModal(false)}
                className="w-full bg-white hover:bg-gray-50 text-gray-600 border-2 border-gray-200 px-4 py-3 rounded-xl font-medium transition-all text-sm"
              >
                Vielleicht später
              </Button>
            </div>

            {/* Privacy Note */}
            <p className="text-[10px] text-gray-500 text-center mt-4 leading-relaxed break-words w-full">
              🔒 Ihre Daten werden vertraulich behandelt und nicht an Dritte weitergegeben
            </p>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;